import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import * as API from '../../store/sagas/api';
import * as userActions from '../../store/actions/user';
import { getOnboardingStepPath, isKycOrLaterOnboardingStep } from '../../utils/onboardingUtils';
import DatePicker from '../../components/DatePicker';
import ErrorMessage from '../../components/ErrorMessage';
import SearchableSelect from '../../components/SearchableSelect';
import SupportedCountrySelect from '../../components/SupportedCountrySelect';
import NewAddressAutocomplete from '../../components/NewAddressAutocomplete';
import { BuildingIcon } from '../../assets/icons';
import { DocumentType } from '../../enums';
import PhoneNumberInput, { isValidE164Phone } from '../../components/PhoneNumberInput';
import { DocumentReviewIcon, ReviewRow, ReviewSection, ReviewStepShell } from '../../components/OnboardingReview';
import { useOnboardingFooter } from '../../components/OnboardingFooterContext';
import {
  COMPANY_DOC_SATISFIES_KEY,
  COMPANY_DOC_TYPE_KEY,
  formatSatisfiesMetadata,
  getSatisfiedSlotsForUpload,
  readDocumentSubtypeFromMetadata,
  toPascalCaseEnumValue,
} from '../../utils/companyDocumentCoverage';
import { buildCompanyOnboardingProfilePayload } from '../../utils/companyOnboardingStorage';

const COMPANY_DOC_METADATA = {
  incorporation: 'incorporation',
  shareholders: 'shareholders',
  natureOfBusiness: 'natureOfBusiness',
  proofAddress: 'proofAddress',
  proofSourceOfFunds: 'proofSourceOfFunds',
};

const COMPANY_INCORPORATION_TYPES = [
  { value: 'CertificateOfIncorporation', label: 'Certificate of incorporation' },
  { value: 'CommercialRegisterExtract', label: 'Commercial register extract' },
  { value: 'ArticlesOfAssociation', label: 'Articles of association' },
  { value: 'CompanyRegistrationCertificate', label: 'Company registration certificate' },
];

const COMPANY_OWNERSHIP_TYPES = [
  { value: 'ShareholderList', label: 'Shareholder register / Gesellschafterliste' },
  { value: 'CapTable', label: 'Cap table' },
  { value: 'CommercialRegisterExtract', label: 'Commercial register extract showing shareholders' },
  { value: 'BeneficialOwnershipRegister', label: 'Official beneficial ownership register extract' },
  { value: 'NotarizedShareholderList', label: 'Notarized shareholder list' },
];

const COMPANY_NATURE_OF_BUSINESS_TYPES = [
  { value: 'Invoices', label: 'Invoices' },
  { value: 'CommercialContracts', label: 'Commercial contracts' },
  { value: 'Receipts', label: 'Receipts' },
  { value: 'BillsOfLading', label: 'Bills of lading or shipping documents' },
];

const COMPANY_PROOF_ADDRESS_TYPES = [
  { value: 'UtilityBill', label: 'Utility bill' },
  { value: 'BankStatement', label: 'Business bank statement' },
  { value: 'OfficeLeaseAgreement', label: 'Office lease agreement' },
  { value: 'TaxDocument', label: 'Tax document' },
  { value: 'GovernmentIssuedLetter', label: 'Government letter' },
];

const COMPANY_SOURCE_OF_FUNDS_TYPES = [
  { value: 'BankStatement', label: 'Bank statement' },
  { value: 'InvestmentDocuments', label: 'Investment documents' },
  { value: 'AuditedFinancialStatements', label: 'Audited financial statements' },
  { value: 'SaleOfAssets', label: 'Sale of assets' },
];

const emptyCompanyFileState = () => ({
  file: null,
  uploaded: false,
  uploading: false,
  pendingLocal: false,
  error: null,
  existingFileName: null,
  documentSubtype: '',
  coveredByKey: null,
  coveredByFileName: null,
  coveredByLabel: null,
});

const COMPANY_FILES = [
  {
    key: 'proofAddress',
    title: 'Proof of address',
    tooltip:
      "Please upload a document confirming your company's registered business address. Acceptable documents: utility bill, business bank statement, office lease agreement, tax document, or government letter. A business bank statement also covers source of funds.",
    metadataValue: COMPANY_DOC_METADATA.proofAddress,
    documentTypes: COMPANY_PROOF_ADDRESS_TYPES,
    recommendedHint: 'Business bank statement is recommended — it can also cover source of funds',
  },
  {
    key: 'incorporation',
    title: 'Incorporation',
    tooltip:
      "Please upload your company's incorporation or registration document. Acceptable documents: certificate of incorporation, commercial register extract, articles of association, or company registration certificate.",
    metadataValue: COMPANY_DOC_METADATA.incorporation,
    documentTypes: COMPANY_INCORPORATION_TYPES,
  },
  {
    key: 'shareholders',
    title: 'Proof of ownership',
    tooltip:
      "Please upload a document showing your company's ownership structure (shareholders and beneficial owners). For German GmbHs, a Gesellschafterliste is typically required because the register extract shows directors, not shareholders.",
    metadataValue: COMPANY_DOC_METADATA.shareholders,
    documentTypes: COMPANY_OWNERSHIP_TYPES,
  },
  {
    key: 'natureOfBusiness',
    title: 'Proof of nature of business',
    tooltip:
      "Please upload a document showing your company's main business activity. Acceptable documents: invoices, commercial contracts, receipts, or bills of lading / shipping documents.",
    metadataValue: COMPANY_DOC_METADATA.natureOfBusiness,
    documentTypes: COMPANY_NATURE_OF_BUSINESS_TYPES,
  },
  {
    key: 'proofSourceOfFunds',
    title: 'Proof of source of funds',
    tooltip:
      'Please upload evidence of the source of the funds you will transact with us. Acceptable documents: bank statement, investment documents, audited financial statements, or sale of assets.',
    metadataValue: COMPANY_DOC_METADATA.proofSourceOfFunds,
    documentTypes: COMPANY_SOURCE_OF_FUNDS_TYPES,
  },
];

function getCompanyDocTitle(key) {
  return COMPANY_FILES.find((f) => f.key === key)?.title || key;
}

function recomputeCompanyFileCoverage(filesState, country) {
  const next = Object.fromEntries(
    COMPANY_FILES.map((f) => [
      f.key,
      {
        ...(filesState?.[f.key] || emptyCompanyFileState()),
        coveredByKey: null,
        coveredByFileName: null,
        coveredByLabel: null,
      },
    ]),
  );

  COMPANY_FILES.forEach((f) => {
    const state = next[f.key];
    if (!state?.uploaded || !state.existingFileName) return;
    const subtype = String(state.documentSubtype || '').trim();
    if (!subtype) return;

    const satisfiedSlots = getSatisfiedSlotsForUpload(f.key, subtype, { country });
    satisfiedSlots.forEach((slotKey) => {
      if (slotKey === f.key) return;
      const target = next[slotKey];
      if (!target) return;
      // Own upload always wins over coverage from another slot.
      if (target.uploaded && target.existingFileName) return;
      next[slotKey] = {
        ...target,
        coveredByKey: f.key,
        coveredByFileName: state.existingFileName,
        coveredByLabel: getCompanyDocTitle(f.key),
      };
    });
  });

  return next;
}

function isCompanyDocSlotSatisfied(state) {
  return !!(state?.uploaded || state?.coveredByKey);
}
const MAX_COMPANY_DOC_BYTES = 10 * 1024 * 1024;
const COMPANY_DOC_ACCEPT = '.pdf,.jpg,.jpeg,.png';
const COMPANY_DOC_ACCEPT_TYPES = ['application/pdf', 'image/jpeg', 'image/png'];

function isAcceptedCompanyDoc(file) {
  if (!file) return false;
  const name = String(file.name || '').toLowerCase();
  const byExt = name.endsWith('.pdf') || name.endsWith('.jpg') || name.endsWith('.jpeg') || name.endsWith('.png');
  const byType = !file.type || COMPANY_DOC_ACCEPT_TYPES.includes(file.type);
  return byExt && byType;
}

const inputClass =
  'h-11 w-full rounded-[10px] border-[1.5px] border-[#DDE6EF] bg-white px-3.5 text-sm text-[#0B1929] outline-none transition placeholder:text-[#7A9AB0]/70 focus:border-[#2E6BAD] focus:ring-[3px] focus:ring-[#2E6BAD]/[.12] disabled:cursor-not-allowed disabled:opacity-50';
const inputErrorBorderClass = '!border-[#C0392B] border-2';
const searchableSelectTriggerClass = (hasError = false) =>
  `${inputClass} flex items-center gap-2 text-left ${hasError ? inputErrorBorderClass : ''}`;
const fieldLabelClass = 'mb-1.5 block text-[11.5px] font-semibold normal-case tracking-normal text-[#7A9AB0]';
const fieldGroupClass = 'mb-4';
const primaryButtonClass =
  'inline-flex h-11 w-full items-center justify-center gap-2 rounded-full bg-[#0B1929] px-5 text-sm font-semibold text-white transition hover:bg-[#0F2540] disabled:cursor-not-allowed disabled:bg-[#B8C9D9]';

const fieldErrorClass = 'mt-1.5 text-xs text-[#C0392B]';
const sofTextareaClass =
  'min-h-[92px] w-full rounded-[10px] border-[1.5px] border-[#DDE6EF] bg-white px-3.5 py-3 text-sm text-[#0B1929] outline-none transition placeholder:text-[#7A9AB0]/70 focus:border-[#2E6BAD] focus:ring-[3px] focus:ring-[#2E6BAD]/[.12]';

function isValidEmail(s) {
  if (!s || typeof s !== 'string') return false;
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(s.trim());
}

function normalizeE164Phone(s) {
  return String(s || '').replace(/\s+/g, '');
}

function isValidWebsite(s) {
  if (!s || typeof s !== 'string') return false;
  const t = s.trim();
  if (!t) return false;
  try {
    const url = t.includes('://') ? t : `https://${t}`;
    const parsed = new URL(url);
    const host = (parsed.hostname || '').toLowerCase();
    if (!host) return false;
    if (host === 'localhost') return true;
    if (host.includes('.')) return true;
    return false;
  } catch {
    return false;
  }
}

function descriptionHasMinLength(s, min = 50) {
  if (s == null || typeof s !== 'string') return false;
  return s.trim().length >= min;
}

const COMPANY_FIELD_ERROR_MESSAGES = {
  description: 'Minimum 50 characters required',
  email: 'Enter a valid email',
  phoneNumber: 'Enter a valid phone number (E.164 format, e.g. +491701234567)',
  website: 'Enter a valid website',
  accountPurposeOther: 'Please explain the account purpose',
  sourceOfFundsOther: 'Please explain the source of funds',
};

function getCompanyFormFieldErrorMessage(key) {
  return COMPANY_FIELD_ERROR_MESSAGES[key] || 'Required';
}

function buildNormalizedPayload(companyForm, operatingAddressDiffers, manualOperatingAddress, existingOnboardingProfile = {}) {
  const regAddr = companyForm?.registeredAddress || {};
  const accountPurpose = (companyForm?.accountPurpose ?? '').trim();
  const sourceOfFunds = (companyForm?.sourceOfFunds ?? '').trim();
  const p = {
    ...companyForm,
    registeredName: (companyForm?.registeredName ?? '').trim(),
    tradeName: (companyForm?.tradeName ?? '').trim(),
    legalForm: (companyForm?.legalForm ?? '').trim(),
    registrationNumber: (companyForm?.registrationNumber ?? '').trim(),
    vatNumber: (companyForm?.vatNumber ?? '').trim(),
    industryNaces: (companyForm?.industryNaces ?? '').trim(),
    industryNaics: (companyForm?.industryNaics ?? '').trim(),
    description: (companyForm?.description ?? '').trim(),
    email: (companyForm?.email ?? '').trim(),
    phoneNumber: normalizeE164Phone((companyForm?.phoneNumber ?? '').trim()),
    website: (companyForm?.website ?? '').trim(),
    registeredAddress: {
      line1: (regAddr.line1 ?? '').trim(),
      line2: (regAddr.line2 ?? '').trim(),
      city: (regAddr.city ?? '').trim(),
      stateRegionProvince: (regAddr.stateRegionProvince ?? '').trim(),
      postCode: (regAddr.postCode ?? '').trim(),
      country: (regAddr.country ?? '').trim(),
    },
    onboardingProfile: {
      ...existingOnboardingProfile,
      accountPurpose,
      accountPurposeOther: accountPurpose === 'Other' ? (companyForm?.accountPurposeOther ?? '').trim() : null,
      sourceOfFunds,
      sourceOfFundsOther: sourceOfFunds === 'Other' ? (companyForm?.sourceOfFundsOther ?? '').trim() : null,
      isDao: existingOnboardingProfile.isDao ?? existingOnboardingProfile.IsDao ?? false,
    },
    isOnboarding: true,
  };
  delete p.accountPurpose;
  delete p.accountPurposeOther;
  delete p.sourceOfFunds;
  delete p.sourceOfFundsOther;
  delete p.isDao;
  p.operatingAddress = operatingAddressDiffers
    ? {
        line1: (manualOperatingAddress?.line1 ?? '').trim(),
        line2: (manualOperatingAddress?.line2 ?? '').trim(),
        city: (manualOperatingAddress?.city ?? '').trim(),
        stateRegionProvince: (manualOperatingAddress?.stateRegionProvince ?? '').trim(),
        postCode: (manualOperatingAddress?.postCode ?? '').trim(),
        country: (manualOperatingAddress?.country ?? '').trim(),
      }
    : null;
  return p;
}

function payloadsEqual(a, b) {
  if (a === b) return true;
  if (!a || !b) return false;
  const keys = new Set([...Object.keys(a), ...Object.keys(b)]);
  return [...keys].every((k) => {
    const va = a[k];
    const vb = b[k];
    if (va == null && vb == null) {
      return true;
    }
    if (typeof va === 'object' && va !== null && typeof vb === 'object' && vb !== null && !Array.isArray(va) && !Array.isArray(vb)) {
      return payloadsEqual(va, vb);
    }
    return JSON.stringify(va) === JSON.stringify(vb);
  });
}

function omitEmptyStrings(obj) {
  if (obj == null || typeof obj !== 'object' || Array.isArray(obj)) return obj;
  return Object.fromEntries(Object.entries(obj).filter(([, v]) => v !== ''));
}

/** Address DB columns that are NOT NULL — keep "" so optional UI fields are not sent as null. */
function normalizeAddressPayload(addr) {
  if (addr == null || typeof addr !== 'object') return addr;
  const cleaned = omitEmptyStrings(addr);
  cleaned.stateRegionProvince = String(addr.stateRegionProvince ?? '').trim();
  return cleaned;
}

function getMergedObject(newValues, oldValues) {
  const old = oldValues || {};
  const merged = { ...old, ...newValues };
  Object.keys(merged).forEach((k) => {
    if (merged[k] === '') delete merged[k];
  });
  if (merged.registeredAddress && typeof merged.registeredAddress === 'object') {
    merged.registeredAddress = normalizeAddressPayload({
      ...(old.registeredAddress || {}),
      ...(newValues.registeredAddress || {}),
    });
  }
  if (newValues.operatingAddress === null) {
    merged.operatingAddress = null;
  } else if (merged.operatingAddress && typeof merged.operatingAddress === 'object') {
    merged.operatingAddress = normalizeAddressPayload({
      ...(old.operatingAddress || {}),
      ...(newValues.operatingAddress || {}),
    });
  }
  const hasChanges = !payloadsEqual(merged, old);
  return [hasChanges, merged];
}

const ACCOUNT_PURPOSE_OPTIONS = [
  { value: 'PayingSuppliers', label: 'Paying suppliers' },
  { value: 'PayingEmployeesOrContractors', label: 'Paying employees or contractors' },
  { value: 'PayingOperatingCosts', label: 'Paying taxes, rent, utilities, or other operating costs' },
  { value: 'InternationalBusinessPayments', label: 'International business payments' },
  { value: 'GroupCompanyTransfers', label: 'Group company transfers' },
  { value: 'Other', label: 'Other — please explain' },
];
const SOURCE_OF_FUNDS_OPTIONS = [
  { value: 'SalesOfGoodsAndServices', label: 'Revenue from sale of goods / services' },
  { value: 'OwnersCapital', label: 'Owner / shareholder capital contribution' },
  { value: 'BusinessLoans', label: 'Business loan or financing' },
  { value: 'InvestmentFunding', label: 'Investment / funding round' },
  { value: 'MarketplacePayouts', label: 'Marketplace or platform payouts' },
  { value: 'IntercompanyTransfers', label: 'Intercompany / group transfers' },
  { value: 'SaleOfBusinessAssets', label: 'Sale of business assets' },
  { value: 'GrantsOrPublicFunding', label: 'Grants or public funding' },
  { value: 'Other', label: 'Other — please explain' },
];
const LEGAL_FORM_OPTIONS = [
  { value: 'GmbH', label: 'GmbH (Limited Liability Company)' },
  { value: 'AG', label: 'AG (Stock Corporation)' },
  { value: 'UG', label: 'UG (Mini-GmbH)' },
  { value: 'OHG', label: 'OHG (General Partnership)' },
  { value: 'KG', label: 'KG (Limited Partnership)' },
  { value: 'GbR', label: 'GbR (Civil Law Partnership)' },
  { value: 'Sole trader / Einzelunternehmer', label: 'Sole trader / Einzelunternehmer' },
  { value: 'SE', label: 'SE (European Company)' },
  { value: 'Ltd', label: 'Ltd (UK)' },
  { value: 'SIA', label: 'SIA (Latvia)' },
  { value: 'UAB', label: 'UAB (Lithuania)' },
  { value: 'OÜ', label: 'OÜ (Estonia)' },
  { value: 'SRL / SARL', label: 'SRL / SARL' },
  { value: 'SAS / SASU', label: 'SAS / SASU' },
  { value: 'BV', label: 'BV (Netherlands)' },
  { value: 'Sp. z o.o.', label: 'Sp. z o.o. (Poland)' },
  { value: 'Other', label: 'Other' },
];

const sectionHeaderClass =
  'mb-3 mt-[22px] flex items-center gap-1.5 border-b border-[#EEF3F8] pb-1.5 text-[10.5px] font-bold uppercase tracking-[0.1em] text-[#7A9AB0]/70 first:mt-6';
const textareaClass =
  'min-h-[110px] w-full resize-none rounded-[10px] border-[1.5px] border-[#DDE6EF] bg-white px-3.5 py-3 text-sm text-[#0B1929] outline-none transition placeholder:text-[#7A9AB0]/70 focus:border-[#2E6BAD] focus:ring-[3px] focus:ring-[#2E6BAD]/[.12] disabled:cursor-not-allowed disabled:opacity-50';

function resolveLegalFormValue(legalForm) {
  const raw = String(legalForm ?? '').trim();
  if (!raw) return '';
  const exact = LEGAL_FORM_OPTIONS.find((opt) => opt.value === raw || opt.label === raw);
  if (exact) return exact.value;
  const byPrefix = LEGAL_FORM_OPTIONS.find((opt) => opt.value && raw.toLowerCase().startsWith(String(opt.value).toLowerCase()));
  return byPrefix?.value || raw;
}

function SectionHeader({ icon, children }) {
  return (
    <div className={sectionHeaderClass}>
      {icon}
      <span>{children}</span>
    </div>
  );
}

SectionHeader.propTypes = {
  icon: PropTypes.node,
  children: PropTypes.node.isRequired,
};

function useIndustryNaceOptions() {
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    let isMounted = true;
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await API.getIndustryNaces();
        if (!isMounted) return;
        const normalized = Array.isArray(data)
          ? data
              .map((item) => ({
                code: item.code ?? item.Code ?? '',
                description: item.description ?? item.Description ?? '',
              }))
              .filter((x) => x.code && x.description)
          : [];
        setOptions(normalized);
      } catch (e) {
        if (isMounted) {
          setError(e);
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    load();

    return () => {
      isMounted = false;
    };
  }, []);

  return { options, loading, error };
}

function SearchableIndustryNaceSelect({ id, value, onChange, hasError }) {
  const { options, loading } = useIndustryNaceOptions();

  const selectOptions = React.useMemo(
    () =>
      (options || []).map((opt) => ({
        value: opt.code,
        label: opt.description,
      })),
    [options],
  );

  const renderSelected = React.useCallback(
    (val, selectedOption, helpers) => {
      const codes = String(val ?? '')
        .split(',')
        .map((c) => c.trim())
        .filter(Boolean);
      if (codes.length === 0) return '';
      const opts = selectOptions || [];
      const canRemoveOne = helpers?.removeValue && codes.length > 1;
      return (
        <>
          {codes.map((code) => {
            const opt = opts.find((o) => String(o.value).trim() === code);
            const label = opt ? opt.label : code;
            return (
              <span
                key={code}
                className='inline-flex max-w-full items-start gap-1.5 rounded-full border border-[#1E4E80] bg-[#EBF4FB] px-3 py-1.5 text-left text-[12.5px] text-[#0B1929]'
              >
                <span className='min-w-0 flex-1 break-words'>
                  {opt ? (
                    <>
                      <span className='font-bold text-[#1E4E80]'>{opt.value}</span>
                      {` ${label}`}
                    </>
                  ) : (
                    code
                  )}
                </span>
                {canRemoveOne && (
                  <span
                    role='button'
                    tabIndex={0}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      helpers.removeValue(code);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        e.stopPropagation();
                        helpers.removeValue(code);
                      }
                    }}
                    className='chip-x shrink-0 cursor-pointer rounded p-0.5 text-[#4E6F8A] hover:text-[#0B1929] focus:outline-none'
                    aria-label={`Remove ${code}`}
                  >
                    ×
                  </span>
                )}
              </span>
            );
          })}
        </>
      );
    },
    [selectOptions],
  );

  const handleChange = (eventOrValue) => {
    if (typeof eventOrValue === 'string') {
      onChange(eventOrValue.trim());
      return;
    }
    const raw = eventOrValue?.target?.value ?? '';
    if (raw === '') {
      onChange('');
      return;
    }
    const trimmed = String(raw).trim();
    if (trimmed.includes(',')) {
      onChange(trimmed);
      return;
    }
    const current = String(value ?? '')
      .split(',')
      .map((c) => c.trim())
      .filter(Boolean);
    const next = current.includes(trimmed) ? value : [...current, trimmed].join(', ');
    onChange(next);
  };

  return (
    <SearchableSelect
      id={id}
      name='industryNaces'
      label='Industry NACE codes'
      required
      placeholder={loading ? 'Loading industry codes…' : 'Search NACE codes…'}
      value={value}
      onChange={handleChange}
      options={selectOptions}
      hasError={hasError}
      searchPlaceholder='Search NACE codes…'
      clearable
      renderSelected={renderSelected}
      selectedMultiline
    />
  );
}

SearchableIndustryNaceSelect.propTypes = {
  id: PropTypes.string,
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  hasError: PropTypes.bool,
};

function mapExternalsAddressToOurs(addr) {
  if (!addr) return null;
  const line1 = addr.street ?? addr.line1 ?? '';
  const city = addr.city ?? '';
  const stateRegionProvince = addr.stateRegionProvince ?? addr.state ?? '';
  const postCode = addr.postalCode ?? addr.postCode ?? '';
  const country = typeof addr.country === 'string' ? addr.country : (addr.country?.alpha2 ?? addr.country?.country ?? '');
  return { line1, line2: addr.line2 ?? '', city, stateRegionProvince, postCode, country };
}

function formatSearchResultAddress(address) {
  if (!address) return null;
  const parts = [address.street, [address.postalCode, address.city].filter(Boolean).join(' ')].filter(Boolean);
  return parts.length ? parts.join(', ') : null;
}

function createEmptyCompanyForm(countryCode = '') {
  return {
    externalId: '',
    signicatCompanyExternalId: '',
    registeredName: '',
    tradeName: '',
    legalForm: '',
    registrationNumber: '',
    registrationDate: undefined,
    vatNumber: '',
    industryNaces: '',
    industryNaics: '',
    description: '',
    email: '',
    phoneNumber: '',
    website: '',
    accountPurpose: '',
    accountPurposeOther: '',
    sourceOfFunds: '',
    sourceOfFundsOther: '',
    registeredAddress: {
      line1: '',
      line2: '',
      city: '',
      stateRegionProvince: '',
      postCode: '',
      country: (countryCode || '').toUpperCase(),
    },
    operatingAddress: null,
    isOnboarding: true,
  };
}

function getRegisteredName(lookupResult) {
  const { names } = lookupResult;
  if (names && typeof names === 'object' && !Array.isArray(names) && names.registered?.name) {
    return names.registered.name;
  }
  if (Array.isArray(names)) {
    const reg = names.find((n) => n?.type === 'registered');
    if (reg?.name) return reg.name;
  }
  return '';
}

function getTradeName(lookupResult) {
  const { names } = lookupResult;
  if (names && typeof names === 'object' && !Array.isArray(names) && names.tradeName?.name) {
    return names.tradeName.name;
  }
  if (Array.isArray(names)) {
    const trade = names.find((n) => n?.type === 'tradeName');
    if (trade?.name) return trade.name;
  }
  return '';
}

function getAddressesByType(lookupResult) {
  const { addresses } = lookupResult;
  if (addresses && typeof addresses === 'object' && !Array.isArray(addresses)) {
    return {
      registered: addresses.registered ?? null,
      business: addresses.business ?? null,
    };
  }
  const arr = Array.isArray(addresses) ? addresses : [];
  const registered = arr.find((a) => a?.type === 'registered') ?? null;
  const business = arr.find((a) => a?.type === 'business') ?? null;
  return { registered, business };
}

function mapLookupToUserCompany(lookupResult, isOnboarding = true, signicatCompanyExternalId = null) {
  if (!lookupResult) return null;
  const { registered: registeredAddr, business: businessAddr } = getAddressesByType(lookupResult);
  const registeredAddress = mapExternalsAddressToOurs(registeredAddr);
  const operatingAddress = mapExternalsAddressToOurs(businessAddr);
  const industryNaces = Array.isArray(lookupResult.industries)
    ? [...new Set(lookupResult.industries.map((i) => i.code).filter(Boolean))].join(', ') || ''
    : '';
  const registeredName = getRegisteredName(lookupResult);
  const tradeName = getTradeName(lookupResult);
  const registrationDateValue = lookupResult.registrationDate;
  let registrationDate = null;
  if (registrationDateValue) {
    registrationDate = typeof registrationDateValue === 'string' ? registrationDateValue.slice(0, 10) : registrationDateValue;
  }

  return {
    externalId: '',
    signicatCompanyExternalId: signicatCompanyExternalId ?? '',
    registeredName,
    tradeName,
    legalForm: lookupResult.legalForm ?? '',
    registrationNumber: lookupResult.registrationNumber ?? '',
    registrationDate: registrationDate || undefined,
    vatNumber: lookupResult.vatNumber ?? '',
    industryNaces,
    industryNaics: '',
    description: '',
    email: lookupResult.email ?? '',
    phoneNumber: lookupResult.phoneNumber ?? '',
    website: '',
    accountPurpose: '',
    accountPurposeOther: '',
    sourceOfFunds: '',
    sourceOfFundsOther: '',
    registeredAddress,
    operatingAddress,
    isOnboarding,
  };
}

function mapUserCompanyToLookup(userCompany) {
  if (!userCompany) return null;
  const regAddr = userCompany.registeredAddress || userCompany.registered_address;
  const opAddr = userCompany.operatingAddress || userCompany.operating_address;
  const toLookupAddress = (addr, type) => {
    if (!addr) return null;
    return {
      type,
      line1: addr.line1 ?? addr.line_1 ?? '',
      line2: addr.line2 ?? addr.line_2 ?? '',
      city: addr.city ?? '',
      state: addr.stateRegionProvince ?? addr.state ?? '',
      postCode: addr.postCode ?? addr.post_code ?? '',
      country: addr.country ?? '',
    };
  };
  const registeredLookupAddress = toLookupAddress(regAddr, 'registered');
  const businessLookupAddress = toLookupAddress(opAddr, 'business');
  const addresses = {};
  if (registeredLookupAddress) addresses.registered = registeredLookupAddress;
  if (businessLookupAddress) addresses.business = businessLookupAddress;

  return {
    name: userCompany.registeredName ?? userCompany.legalName ?? userCompany.name ?? '',
    names: {
      registered: {
        name: userCompany.registeredName ?? userCompany.legalName ?? userCompany.name ?? '',
        type: 'registered',
      },
      ...(userCompany.tradeName ? { tradeName: { name: userCompany.tradeName, type: 'tradeName' } } : {}),
    },
    legalForm: userCompany.legalForm ?? '',
    registrationNumber: userCompany.registrationNumber ?? '',
    registrationDate: userCompany.registrationDate ?? null,
    vatNumber: userCompany.vatNumber ?? '',
    industries: [
      ...new Set(
        String(userCompany.industryNaces ?? '')
          .split(',')
          .map((code) => code.trim())
          .filter(Boolean),
      ),
    ].map((code) => ({ code })),
    email: userCompany.email ?? '',
    phoneNumber: userCompany.phoneNumber ?? '',
    addresses,
  };
}

function mapUserCompanyToForm(userCompany) {
  if (!userCompany) return null;
  const regAddr = userCompany.registeredAddress || userCompany.registered_address;
  const opAddr = userCompany.operatingAddress || userCompany.operating_address;
  const onboardingProfile = userCompany.onboardingProfile || userCompany.OnboardingProfile || {};
  const registrationDateValue = userCompany.registrationDate ?? userCompany.registration_date;
  let registrationDate = '';
  if (registrationDateValue) {
    registrationDate = typeof registrationDateValue === 'string' ? registrationDateValue.slice(0, 10) : String(registrationDateValue).slice(0, 10);
  }
  return {
    externalId: userCompany.externalId ?? userCompany.external_id ?? '',
    signicatCompanyExternalId: userCompany.signicatCompanyExternalId ?? userCompany.signicat_company_external_id ?? '',
    registeredName: userCompany.registeredName ?? userCompany.registered_name ?? userCompany.legalName ?? userCompany.name ?? '',
    tradeName: userCompany.tradeName ?? userCompany.trade_name ?? '',
    legalForm: userCompany.legalForm ?? userCompany.legal_form ?? '',
    registrationNumber: userCompany.registrationNumber ?? userCompany.registration_number ?? '',
    registrationDate: registrationDate || undefined,
    vatNumber: userCompany.vatNumber ?? userCompany.vat_number ?? '',
    industryNaces: [
      ...new Set(
        String(userCompany.industryNaces ?? userCompany.industry_naces ?? '')
          .split(',')
          .map((c) => c.trim())
          .filter(Boolean),
      ),
    ].join(', '),
    industryNaics: userCompany.industryNaics ?? userCompany.industry_naics ?? '',
    description: userCompany.description ?? '',
    email: userCompany.email ?? '',
    phoneNumber: userCompany.phoneNumber ?? userCompany.phone_number ?? '',
    website: userCompany.website ?? '',
    accountPurpose:
      onboardingProfile.accountPurpose ??
      onboardingProfile.AccountPurpose ??
      userCompany.accountPurpose ??
      userCompany.account_purpose ??
      '',
    accountPurposeOther:
      onboardingProfile.accountPurposeOther ??
      onboardingProfile.AccountPurposeOther ??
      userCompany.accountPurposeOther ??
      userCompany.account_purpose_other ??
      '',
    sourceOfFunds:
      onboardingProfile.sourceOfFunds ??
      onboardingProfile.SourceOfFunds ??
      userCompany.sourceOfFunds ??
      userCompany.source_of_funds ??
      '',
    sourceOfFundsOther:
      onboardingProfile.sourceOfFundsOther ??
      onboardingProfile.SourceOfFundsOther ??
      userCompany.sourceOfFundsOther ??
      userCompany.source_of_funds_other ??
      '',
    registeredAddress: regAddr
      ? {
          line1: regAddr.line1 ?? regAddr.line_1 ?? '',
          line2: regAddr.line2 ?? regAddr.line_2 ?? '',
          city: regAddr.city ?? '',
          stateRegionProvince: regAddr.stateRegionProvince ?? '',
          postCode: regAddr.postCode ?? regAddr.post_code ?? '',
          country: regAddr.country ?? '',
        }
      : null,
    operatingAddress: opAddr
      ? {
          line1: opAddr.line1 ?? opAddr.line_1 ?? '',
          line2: opAddr.line2 ?? opAddr.line_2 ?? '',
          city: opAddr.city ?? '',
          stateRegionProvince: opAddr.stateRegionProvince ?? '',
          postCode: opAddr.postCode ?? opAddr.post_code ?? '',
          country: opAddr.country ?? '',
        }
      : null,
    isOnboarding: true,
  };
}

function CollectDataCompany() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const setFooterConfig = useOnboardingFooter();
  const hasJustConfirmedRef = useRef(false);
  const hasInitializedFromUserRef = useRef(false);
  const hasSkippedSearchForExistingCompanyRef = useRef(false);
  const hasInitializedOperatingAddressRef = useRef(false);
  const companyDetailsSectionRef = useRef(null);
  const hasAutoScrolledToDetailsRef = useRef(false);
  const { user, onboardingStatus, advancingOnboardingStep, advanceOnboardingStepError, updatingUser, updateUserError } = useSelector(
    (state) => state.user,
  );
  const isCollectDataStepCompleted = React.useMemo(() => {
    if (!Array.isArray(onboardingStatus?.steps)) return false;
    return onboardingStatus.steps.some(
      (s) => typeof s === 'object' && s !== null && (s?.step === 'CollectData' || s?.Step === 'CollectData') && s?.isCompleted === true,
    );
  }, [onboardingStatus?.steps]);

  const isKycOrLaterStep = isKycOrLaterOnboardingStep(onboardingStatus?.currentStep);
  const isLocked = isKycOrLaterStep;
  const allowedOnboardingCountries = useSelector((state) => state.config?.allowedOnboardingCountries ?? []);
  const { options: industryNaceOptions } = useIndustryNaceOptions();
  const [companyName, setCompanyName] = useState('');
  const [country, setCountry] = useState('');
  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState(null);
  const [searchResults, setSearchResults] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [lookingUp, setLookingUp] = useState(false);
  const [lookupError, setLookupError] = useState(null);
  const [lookupResult, setLookupResult] = useState(null);
  const initialCollectSubstep = location.state?.companyCollectSubstep;
  const hasSavedCompanyDataAtInit = !!(
    user?.company?.registeredName ||
    user?.company?.registrationNumber ||
    user?.company?.registeredAddress ||
    user?.company?.operatingAddress
  );
  const [showCompanyDetailsScreen, setShowCompanyDetailsScreen] = useState(() => {
    if (initialCollectSubstep === 'company' || initialCollectSubstep === 'documents' || initialCollectSubstep === 'review') {
      return true;
    }
    return hasSavedCompanyDataAtInit;
  });
  const [showCompanyFiles, setShowCompanyFiles] = useState(() => initialCollectSubstep === 'documents');
  const [showSummary, setShowSummary] = useState(() => initialCollectSubstep === 'review');

  useEffect(() => {
    let nextSubstep = 'search';
    if (showSummary) nextSubstep = 'review';
    else if (showCompanyFiles) nextSubstep = 'documents';
    else if (showCompanyDetailsScreen) nextSubstep = 'company';

    if (location.state?.companyCollectSubstep === nextSubstep) return;
    navigate(location.pathname, {
      replace: true,
      state: { ...(location.state ?? {}), companyCollectSubstep: nextSubstep },
    });
  }, [location.pathname, location.state, navigate, showCompanyDetailsScreen, showCompanyFiles, showSummary]);
  const [saving, setSaving] = useState(false);
  const [operatingAddressDiffers, setOperatingAddressDiffers] = useState(false);
  const [operatingAddressError, setOperatingAddressError] = useState(null);
  const [operatingAddressSearch, setOperatingAddressSearch] = useState('');
  const [companyForm, setCompanyForm] = useState(() => {
    if (!hasSavedCompanyDataAtInit || !user?.company) return null;
    return mapUserCompanyToForm(user.company);
  });
  const [companyFormFieldErrors, setCompanyFormFieldErrors] = useState(() => new Set());
  const [regAddrFieldErrors, setRegAddrFieldErrors] = useState(() => new Set());
  const [manualOperatingAddress, setManualOperatingAddress] = useState({
    line1: '',
    line2: '',
    city: '',
    stateRegionProvince: '',
    postCode: '',
    country: '',
  });

  const handleOperatingAddressSearchSelect = (selected) => {
    const components = selected?.components || {};
    const streetLine = [components.route, components.streetNumber].filter(Boolean).join(' ').trim();
    const nextCity = (components.city || '').trim();
    const nextState = (components.state || '').trim();
    const nextPost = (components.postalCode || '').trim();
    const nextCountry = (components.country || '').trim().toUpperCase();
    const full = (selected?.fullAddress || '').trim();

    setOperatingAddressSearch(full);
    setManualOperatingAddress((prev) => ({
      ...prev,
      line1: streetLine || full || prev.line1,
      city: nextCity || prev.city,
      stateRegionProvince: nextState || prev.stateRegionProvince,
      postCode: nextPost || prev.postCode,
      country: nextCountry || prev.country,
    }));
  };
  const [companyFiles, setCompanyFiles] = useState(() =>
    Object.fromEntries(COMPANY_FILES.map((f) => [f.key, emptyCompanyFileState()])),
  );
  const [sourceOfFundsDescription, setSourceOfFundsDescription] = useState('');
  const [sofFieldErrors, setSofFieldErrors] = useState(() => new Set());

  useEffect(() => {
    const profile = user?.company?.onboardingProfile ?? user?.company?.OnboardingProfile ?? {};
    setSourceOfFundsDescription(profile.sourceOfFundsDescription ?? profile.SourceOfFundsDescription ?? '');
  }, [user?.company?.OnboardingProfile, user?.company?.onboardingProfile]);

  const companyDocsCountry =
    companyForm?.registeredAddress?.country ||
    user?.company?.registeredAddress?.country ||
    user?.company?.operatingAddress?.country ||
    country ||
    '';

  const hasExistingCompany = !!(
    user?.company?.registeredName ||
    user?.company?.registrationNumber ||
    user?.company?.registeredAddress ||
    user?.company?.operatingAddress ||
    user?.company?.externalId
  );

  const countryOptions = React.useMemo(() => {
    if (allowedOnboardingCountries && allowedOnboardingCountries.length > 0) {
      return allowedOnboardingCountries.map((c) => ({
        value: (c.code || '').toLowerCase(),
        label: c.name || c.code || '',
        searchText: `${c.name || ''} ${c.code || ''}`.trim(),
      }));
    }
    return [];
  }, [allowedOnboardingCountries]);

  React.useEffect(() => {
    if (countryOptions.length > 0 && !countryOptions.some((o) => o.value === country)) {
      setCountry(countryOptions[0].value);
    }
  }, [countryOptions, country]);

  const [companyFilesError, setCompanyFilesError] = useState(null);

  const handleSearch = async (e) => {
    e?.preventDefault();
    if (!companyName?.trim()) return;
    setSearchError(null);
    setSearchResults([]);
    setSelectedCompany(null);
    setLookupResult(null);
    setSearching(true);
    try {
      const results = await API.companySearch({
        companyName: companyName.trim(),
        country: country.toLowerCase(),
      });
      const list = Array.isArray(results) ? results : [];
      setSearchResults(list);
      if (list.length === 1) {
        setSelectedCompany(list[0]);
      }
    } catch (err) {
      setSearchError(err?.message || err?.detail || 'Search failed. Please try again.');
    } finally {
      setSearching(false);
    }
  };

  const handleSelectCompany = (company) => {
    if (lookingUp) return;
    setSelectedCompany(company);
    setLookupError(null);
  };

  const handleUseCompany = (company) => {
    if (!company || lookingUp) return;
    setSelectedCompany(company);
    setLookupError(null);
    setLookupResult(null);
    const savedExternalId = user?.company?.externalId ?? 'saved-company';
    if (user?.company && (company.externalId === user.company.externalId || company.externalId === savedExternalId)) {
      const form = mapUserCompanyToForm(user.company);
      if (form) {
        setCompanyForm(form);
        setShowCompanyDetailsScreen(true);
      }
      return;
    }
    setLookingUp(true);
    API.companyLookup({ companyExternalId: company.externalId, country: country.toLowerCase() })
      .then((result) => {
        setLookupResult(result);
        setCompanyForm(mapLookupToUserCompany(result, true, company.externalId));
        setShowCompanyDetailsScreen(true);
      })
      .catch((err) => {
        setLookupError(err?.message || err?.detail || 'Lookup failed. Please try again.');
      })
      .finally(() => {
        setLookingUp(false);
      });
  };

  const handleEnterManually = () => {
    if (lookingUp || isLocked) return;
    setSelectedCompany(null);
    setLookupResult(null);
    setLookupError(null);
    setCompanyForm(createEmptyCompanyForm(country));
    setShowCompanyDetailsScreen(true);
  };

  const handleSaveAndContinue = useCallback(() => {
    if (isLocked) return;
    if (!companyForm) return;
    const requiredFields = [
      'registeredName',
      'legalForm',
      'registrationNumber',
      'registrationDate',
      'vatNumber',
      'industryNaces',
      'description',
      'email',
      'phoneNumber',
      'website',
      'accountPurpose',
      'sourceOfFunds',
    ];
    const missing = requiredFields.filter((key) => !String(companyForm[key] ?? '').trim());
    if (companyForm.accountPurpose === 'Other' && !String(companyForm.accountPurposeOther ?? '').trim()) {
      missing.push('accountPurposeOther');
    }
    if (companyForm.sourceOfFunds === 'Other' && !String(companyForm.sourceOfFundsOther ?? '').trim()) {
      missing.push('sourceOfFundsOther');
    }
    const regAddr = companyForm.registeredAddress || {};
    const regAddrRequired = ['line1', 'city', 'stateRegionProvince', 'postCode', 'country'];
    const missingAddr = regAddrRequired.filter((key) => !String(regAddr[key] ?? '').trim());
    const invalidFormat = new Set();
    if (companyForm.description != null && companyForm.description.trim() && !descriptionHasMinLength(companyForm.description)) {
      invalidFormat.add('description');
    }
    if (companyForm.email != null && companyForm.email.trim() && !isValidEmail(companyForm.email)) {
      invalidFormat.add('email');
    }
    if (companyForm.phoneNumber != null && companyForm.phoneNumber.trim() && !isValidE164Phone(companyForm.phoneNumber)) {
      invalidFormat.add('phoneNumber');
    }
    if (companyForm.website != null && companyForm.website.trim() && !isValidWebsite(companyForm.website)) {
      invalidFormat.add('website');
    }
    const allFieldErrors = new Set([...missing, ...invalidFormat]);
    if (allFieldErrors.size > 0 || missingAddr.length > 0) {
      setCompanyFormFieldErrors(allFieldErrors);
      setRegAddrFieldErrors(new Set(missingAddr));
      return;
    }
    setCompanyFormFieldErrors(new Set());
    setRegAddrFieldErrors(new Set());
    const oldCompany = user?.company || {};
    const existingOnboardingProfile = oldCompany.onboardingProfile ?? oldCompany.OnboardingProfile ?? {};
    const fullPayload = buildNormalizedPayload(
      companyForm,
      operatingAddressDiffers,
      manualOperatingAddress,
      existingOnboardingProfile,
    );
    if (operatingAddressDiffers) {
      const oa = fullPayload.operatingAddress;
      if (
        !oa?.line1?.trim() ||
        !oa?.city?.trim() ||
        !oa?.stateRegionProvince?.trim() ||
        !oa?.postCode?.trim() ||
        !oa?.country?.trim()
      ) {
        setOperatingAddressError(
          'Please enter line 1, city, state / region / province, post code, and country for operating address.',
        );
        return;
      }
    }
    setOperatingAddressError(null);

    const [hasChanges, mergedPayload] = getMergedObject(fullPayload, oldCompany);
    if (hasExistingCompany && !hasChanges) {
      setShowSummary(false);
      setShowCompanyFiles(true);
      return;
    }
    setSaving(true);
    dispatch(userActions.updateCompany(mergedPayload));
  }, [companyForm, dispatch, hasExistingCompany, isLocked, manualOperatingAddress, operatingAddressDiffers, user?.company]);

  React.useEffect(() => {
    if (saving && !updatingUser && !updateUserError) {
      setSaving(false);
      setShowSummary(false);
      setShowCompanyFiles(true);
    }
    if (saving && !updatingUser && updateUserError) {
      setSaving(false);
    }
  }, [saving, updatingUser, updateUserError]);

  React.useEffect(() => {
    if (!lookupResult) {
      return;
    }

    if (!companyForm) {
      setCompanyForm(mapLookupToUserCompany(lookupResult, true, selectedCompany?.externalId ?? null));
    }

    const { business } = getAddressesByType(lookupResult);
    const mappedBusiness = mapExternalsAddressToOurs(business);
    const hasBusiness = !!mappedBusiness;
    if (hasBusiness) {
      setOperatingAddressDiffers(true);
      setManualOperatingAddress({
        line1: mappedBusiness.line1 ?? '',
        line2: mappedBusiness.line2 ?? '',
        city: mappedBusiness.city ?? '',
        stateRegionProvince: mappedBusiness.stateRegionProvince ?? '',
        postCode: mappedBusiness.postCode ?? '',
        country: mappedBusiness.country ?? '',
      });
    } else {
      setOperatingAddressDiffers(false);
      setManualOperatingAddress({
        line1: '',
        line2: '',
        city: '',
        stateRegionProvince: '',
        postCode: '',
        country: '',
      });
    }

    setOperatingAddressError(null);
  }, [lookupResult]);

  useEffect(() => {
    if (hasInitializedOperatingAddressRef.current) return;
    if (lookupResult) return;
    if (!showCompanyDetailsScreen) return;
    if (!companyForm) return;

    hasInitializedOperatingAddressRef.current = true;

    const opAddr = companyForm.operatingAddress;
    const hasOperatingAddress = !!(opAddr && (opAddr.line1 || opAddr.line2 || opAddr.city || opAddr.stateRegionProvince || opAddr.postCode || opAddr.country));

    if (hasOperatingAddress) {
      setOperatingAddressDiffers(true);
      setManualOperatingAddress({
        line1: opAddr.line1 ?? '',
        line2: opAddr.line2 ?? '',
        city: opAddr.city ?? '',
        stateRegionProvince: opAddr.stateRegionProvince ?? '',
        postCode: opAddr.postCode ?? '',
        country: opAddr.country ?? '',
      });
    } else {
      setOperatingAddressDiffers(false);
      setManualOperatingAddress({
        line1: '',
        line2: '',
        city: '',
        stateRegionProvince: '',
        postCode: '',
        country: '',
      });
    }

    setOperatingAddressError(null);
  }, [companyForm, lookupResult, showCompanyDetailsScreen]);

  React.useEffect(() => {
    const hasSavedCompanyData = !!(
      user?.company?.registeredName ||
      user?.company?.registrationNumber ||
      user?.company?.registeredAddress ||
      user?.company?.operatingAddress
    );
    const isReturningFromLaterStep = !!(onboardingStatus?.currentStep && onboardingStatus.currentStep !== 'CollectData');
    if (!hasInitializedFromUserRef.current && !lookupResult && !showCompanyFiles && !showSummary && hasSavedCompanyData && isReturningFromLaterStep) {
      hasInitializedFromUserRef.current = true;
      const selected = {
        externalId: user.company.externalId ?? 'saved-company',
        name: user.company.registeredName ?? user.company.legalName ?? user.company.name ?? '',
        organizationNumber: user.company.registrationNumber ?? '',
      };
      setCompanyName(selected.name);
      setSearchResults([selected]);
      setSelectedCompany(selected);
      setLookupResult(mapUserCompanyToLookup(user.company));
    }
  }, [lookupResult, onboardingStatus?.currentStep, showCompanyFiles, showSummary, user?.company, user?.company?.externalId]);

  React.useEffect(() => {
    const hasSavedCompanyData = !!(
      user?.company?.registeredName ||
      user?.company?.registrationNumber ||
      user?.company?.registeredAddress ||
      user?.company?.operatingAddress
    );
    const requestedSubstep = location.state?.companyCollectSubstep;

    if (requestedSubstep === 'documents' || requestedSubstep === 'review' || requestedSubstep === 'company') {
      if (!companyForm && user?.company) {
        const form = mapUserCompanyToForm(user.company);
        if (form) setCompanyForm(form);
      }
      return;
    }

    if (
      !hasSkippedSearchForExistingCompanyRef.current &&
      hasSavedCompanyData &&
      user?.company &&
      !showCompanyDetailsScreen &&
      !showCompanyFiles &&
      !showSummary
    ) {
      hasSkippedSearchForExistingCompanyRef.current = true;
      const form = mapUserCompanyToForm(user.company);
      if (form) {
        setCompanyForm(form);
        setShowCompanyDetailsScreen(true);
      }
    }
  }, [companyForm, location.state?.companyCollectSubstep, showCompanyDetailsScreen, showCompanyFiles, showSummary, user?.company]);

  const handleConfirmData = useCallback(() => {
    const shouldAdvanceCollectData = onboardingStatus?.currentStep === 'CollectData' && !isCollectDataStepCompleted;

    if (shouldAdvanceCollectData) {
      hasJustConfirmedRef.current = true;
      dispatch(userActions.advanceOnboardingStep('CollectData'));
      return;
    }

    const nextPath = onboardingStatus?.currentStep ? getOnboardingStepPath(onboardingStatus.currentStep) : null;
    if (nextPath) {
      navigate(nextPath);
    }
  }, [dispatch, isCollectDataStepCompleted, navigate, onboardingStatus?.currentStep]);

  const handleBackToCompanyFiles = useCallback(() => {
    setShowSummary(false);
    setShowCompanyFiles(true);
    setShowCompanyDetailsScreen(true);
  }, []);

  const handleBackFromCompanyFiles = useCallback(() => {
    setShowCompanyFiles(false);
    setShowSummary(false);
    setShowCompanyDetailsScreen(true);
  }, []);

  const handleBackFromCompanyDetails = useCallback(() => {
    hasSkippedSearchForExistingCompanyRef.current = true;
    setShowSummary(false);
    setShowCompanyFiles(false);
    setShowCompanyDetailsScreen(false);
  }, []);

  const handleCompanyFilesContinue = useCallback(() => {
    const nextSofErrors = new Set();
    if (!String(sourceOfFundsDescription || '').trim()) nextSofErrors.add('sourceOfFundsDescription');
    setSofFieldErrors(nextSofErrors);
    if (nextSofErrors.size > 0) return;

    const existingProfile = user?.company?.onboardingProfile ?? user?.company?.OnboardingProfile ?? {};
    const profile = buildCompanyOnboardingProfilePayload({
      ...existingProfile,
      sourceOfFundsDescription: String(sourceOfFundsDescription || '').trim(),
    });
    dispatch(userActions.upsertCompanyOnboardingProfile(profile));

    setShowCompanyFiles(false);
    setShowCompanyDetailsScreen(true);
    setShowSummary(true);
  }, [dispatch, sourceOfFundsDescription, user?.company]);

  const allCompanyDocsSatisfied = COMPANY_FILES.every((f) => isCompanyDocSlotSatisfied(companyFiles[f.key]));
  const sofQuestionsComplete = String(sourceOfFundsDescription || '').trim().length > 0;
  const canContinueCompanyFiles = allCompanyDocsSatisfied && sofQuestionsComplete;

  const handleCompanyFileSelect = async (key, file, documentSubtypeOverride) => {
    if (isLocked) return;
    if (!file) return;
    setCompanyFilesError(null);
    const doc = COMPANY_FILES.find((d) => d.key === key);
    const requiresSubtype = Array.isArray(doc?.documentTypes) && doc.documentTypes.length > 0;
    const documentSubtype = String(
      documentSubtypeOverride ?? companyFiles?.[key]?.documentSubtype ?? '',
    ).trim();

    if (!isAcceptedCompanyDoc(file)) {
      setCompanyFiles((prev) => ({
        ...prev,
        [key]: { ...prev[key], error: 'Only PDF, JPG or PNG files are accepted.' },
      }));
      return;
    }
    if (file.size > MAX_COMPANY_DOC_BYTES) {
      setCompanyFiles((prev) => ({
        ...prev,
        [key]: { ...prev[key], error: 'File must be 10 MB or smaller.' },
      }));
      return;
    }

    if (requiresSubtype && !documentSubtype) {
      const previousFileName = companyFiles?.[key]?.existingFileName;
      setCompanyFiles((prev) => {
        const withPending = {
          ...prev,
          [key]: {
            ...prev[key],
            file,
            uploading: false,
            uploaded: false,
            pendingLocal: true,
            error: null,
            existingFileName: null,
            coveredByKey: null,
            coveredByFileName: null,
            coveredByLabel: null,
          },
        };
        return recomputeCompanyFileCoverage(withPending, companyDocsCountry);
      });
      if (previousFileName) {
        API.deleteDocument(DocumentType.Compliance, previousFileName).catch(() => {});
      }
      return;
    }

    setCompanyFiles((prev) => ({
      ...prev,
      [key]: {
        ...prev[key],
        file,
        documentSubtype,
        uploading: true,
        uploaded: false,
        pendingLocal: false,
        error: null,
      },
    }));
    try {
      const existingFileName = companyFiles?.[key]?.existingFileName;
      if (existingFileName) {
        await API.deleteDocument(DocumentType.Compliance, existingFileName);
      }
      const satisfiedSlots = getSatisfiedSlotsForUpload(key, documentSubtype, { country: companyDocsCountry });
      const formData = new FormData();
      formData.append('file', file);
      formData.append('metadata[type]', doc.metadataValue);
      formData.append(`metadata[${COMPANY_DOC_SATISFIES_KEY}]`, formatSatisfiesMetadata(satisfiedSlots));
      if (requiresSubtype && documentSubtype) {
        formData.append(`metadata[${COMPANY_DOC_TYPE_KEY}]`, documentSubtype);
      }
      const uploaded = await API.uploadDocument(formData, DocumentType.Compliance);
      const uploadedName = uploaded?.name ?? uploaded?.Name ?? file?.name ?? null;
      setCompanyFiles((prev) => {
        const withUpload = {
          ...prev,
          [key]: {
            ...prev[key],
            uploading: false,
            uploaded: true,
            pendingLocal: false,
            error: null,
            existingFileName: uploadedName,
            file: null,
            coveredByKey: null,
            coveredByFileName: null,
            coveredByLabel: null,
          },
        };
        return recomputeCompanyFileCoverage(withUpload, companyDocsCountry);
      });
    } catch (err) {
      setCompanyFiles((prev) => ({
        ...prev,
        [key]: {
          ...prev[key],
          uploading: false,
          pendingLocal: true,
          error: err?.message || err?.detail || 'Upload failed.',
        },
      }));
    }
  };

  const handleCompanyDocumentSubtypeChange = (key, nextSubtype) => {
    if (isLocked) return;
    const subtype = String(nextSubtype || '').trim();
    const pendingFile = companyFiles?.[key]?.file;

    setCompanyFiles((prev) => {
      const withSubtype = {
        ...prev,
        [key]: {
          ...(prev[key] || emptyCompanyFileState()),
          documentSubtype: subtype,
          error: null,
        },
      };
      return recomputeCompanyFileCoverage(withSubtype, companyDocsCountry);
    });

    if (pendingFile && subtype) {
      handleCompanyFileSelect(key, pendingFile, subtype);
    }
  };

  useEffect(() => {
    if (showSummary) {
      setFooterConfig({
        continueLabel: advancingOnboardingStep ? 'Confirming…' : 'Confirm',
        continueDisabled: advancingOnboardingStep,
        onContinue: handleConfirmData,
        backLabel: 'Back',
        onBack: handleBackToCompanyFiles,
      });
      return () => setFooterConfig({});
    }

    if (showCompanyFiles) {
      setFooterConfig({
        continueLabel: 'Continue',
        continueDisabled: !allCompanyDocsSatisfied,
        onContinue: handleCompanyFilesContinue,
        backLabel: 'Back',
        onBack: handleBackFromCompanyFiles,
      });
      return () => setFooterConfig({});
    }

    if (showCompanyDetailsScreen && companyForm) {
      setFooterConfig({
        continueLabel: saving || updatingUser ? 'Saving…' : 'Continue',
        continueDisabled: saving || updatingUser || isLocked,
        onContinue: handleSaveAndContinue,
        backLabel: 'Back',
        onBack: handleBackFromCompanyDetails,
      });
      return () => setFooterConfig({});
    }

    setFooterConfig({});
    return undefined;
  }, [
    advancingOnboardingStep,
    canContinueCompanyFiles,
    companyForm,
    handleBackFromCompanyDetails,
    handleBackFromCompanyFiles,
    handleBackToCompanyFiles,
    handleCompanyFilesContinue,
    handleConfirmData,
    handleSaveAndContinue,
    isLocked,
    saving,
    setFooterConfig,
    showCompanyDetailsScreen,
    showCompanyFiles,
    showSummary,
    updatingUser,
  ]);

  useEffect(() => {
    let isMounted = true;
    const loadExistingDocuments = async () => {
      try {
        const docs = await API.getDocuments(DocumentType.Compliance);
        if (!isMounted) return;

        const list = Array.isArray(docs) ? docs : [];
        const byType = new Map();
        list.forEach((d) => {
          const metaType = d?.metadata?.type ?? d?.Metadata?.type ?? null;
          if (metaType && !byType.has(metaType)) {
            byType.set(metaType, d);
          }
        });

        setCompanyFiles((prev) => {
          const next = { ...prev };
          COMPANY_FILES.forEach((f) => {
            const match = byType.get(f.metadataValue);
            if (match?.name || match?.Name) {
              const fileName = match.name ?? match.Name;
              const meta = match?.metadata ?? match?.Metadata ?? {};
              const documentSubtype = readDocumentSubtypeFromMetadata(meta);
              next[f.key] = {
                ...next[f.key],
                uploaded: true,
                uploading: false,
                pendingLocal: false,
                error: null,
                existingFileName: fileName,
                file: null,
                documentSubtype: toPascalCaseEnumValue(documentSubtype),
                coveredByKey: null,
                coveredByFileName: null,
                coveredByLabel: null,
              };
            } else {
              next[f.key] = {
                ...emptyCompanyFileState(),
                documentSubtype: next[f.key]?.documentSubtype || '',
              };
            }
          });
          return recomputeCompanyFileCoverage(next, companyDocsCountry);
        });
      } catch (e) {
        if (isMounted) {
          setCompanyFilesError(e?.message || e?.detail || 'Failed to load uploaded documents.');
        }
      }
    };

    if (showCompanyFiles) {
      loadExistingDocuments();
    }

    return () => {
      isMounted = false;
    };
  }, [companyDocsCountry, showCompanyFiles]);

  useEffect(() => {
    setCompanyFiles((prev) => recomputeCompanyFileCoverage(prev, companyDocsCountry));
  }, [companyDocsCountry]);

  React.useEffect(() => {
    if (hasJustConfirmedRef.current && !advancingOnboardingStep && onboardingStatus?.currentStep && onboardingStatus.currentStep !== 'CollectData') {
      const nextPath = getOnboardingStepPath(onboardingStatus.currentStep);
      if (nextPath) {
        hasJustConfirmedRef.current = false;
        navigate(nextPath);
      }
    }
  }, [advancingOnboardingStep, onboardingStatus?.currentStep, navigate]);

  useEffect(() => {
    if (!showCompanyDetailsScreen || !companyForm) {
      hasAutoScrolledToDetailsRef.current = false;
      return;
    }
    if (hasAutoScrolledToDetailsRef.current) return;
    const el = companyDetailsSectionRef.current;
    if (!el) return;
    el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    hasAutoScrolledToDetailsRef.current = true;
  }, [showCompanyDetailsScreen, companyForm]);

  if (!user) {
    return null;
  }

  const displayCompany = user?.company || (lookupResult && mapLookupToUserCompany(lookupResult, true, selectedCompany?.externalId ?? null));

  if (showCompanyFiles) {
    return (
      <div className='flex w-full justify-center bg-[#EBF4FB] px-4 pb-28 pt-7 font-sans text-[#0B1929] sm:min-h-screen sm:px-6'>
        <div className='w-full min-w-0 max-w-[540px]'>
          <div className='rounded-[20px] bg-white px-6 pb-10 pt-8 shadow-[0_4px_32px_rgba(11,25,41,0.08)] sm:px-10 sm:pb-11 sm:pt-9'>
            <div className='mx-auto mb-5 flex h-14 w-14 items-center justify-center rounded-full bg-[#EBF4FB] text-[#1E4E80]'>
              <svg width='26' height='26' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='1.75' strokeLinecap='round' strokeLinejoin='round' aria-hidden='true'>
                <path d='M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z' />
              </svg>
            </div>
            <h1 className='mb-2.5 text-center font-serif text-2xl font-normal leading-[1.25] text-[#0B1929]'>Company Documents.</h1>
            <p className='mt-2 text-center text-sm leading-6 text-[#4E6F8A]'>
              Upload the required documents below. One document can cover related requirements — fields stay visible so you can still replace them.
            </p>

            <fieldset disabled={isLocked} className='mt-7 min-w-0 space-y-3 border-0 p-0'>
              {companyFilesError && <ErrorMessage error={companyFilesError} onDismiss={() => setCompanyFilesError(null)} className='mb-1' />}

              {COMPANY_FILES.map(({ key, title, tooltip, documentTypes, recommendedHint }) => {
                const state = companyFiles[key] || emptyCompanyFileState();
                const hasPendingLocal = !!(state.file || state.pendingLocal) && !state.existingFileName;
                const isDone = !!state.uploaded || hasPendingLocal;
                const isCovered = !isDone && !!state.coveredByKey;
                const isSatisfied = !!state.uploaded || isCovered;
                const isUploading = state.uploading;
                const fileInputId = `company-file-${key}`;
                const subtypeSelectId = `company-file-subtype-${key}`;
                const displayName = state.existingFileName || state.file?.name || 'File uploaded';
                const hasDocumentTypes = Array.isArray(documentTypes) && documentTypes.length > 0;
                const missingType = hasDocumentTypes && hasPendingLocal && !String(state.documentSubtype || '').trim();
                let uploadZoneClass = 'border-[#C9D8E6] bg-[#F8FAFB] hover:border-[#2E6BAD] hover:bg-[#EBF4FB]/50';
                if (isDone || isCovered) {
                  uploadZoneClass = 'border-[#A8D5B5] bg-[#F3FAF5]';
                } else if (isUploading) {
                  uploadZoneClass = 'cursor-wait border-[#B8C9D9] bg-[#F8FAFB]';
                }

                let statusLabel = null;
                let statusClass = 'shrink-0 pt-0.5 text-[11px] font-medium uppercase tracking-[0.06em]';
                if (isSatisfied) {
                  if (isCovered) {
                    statusLabel = key === 'proofSourceOfFunds' ? null : 'Covered';
                  } else {
                    statusLabel = 'Uploaded';
                  }
                  statusClass += ' text-[#1F7A45]';
                } else if (hasPendingLocal) {
                  statusLabel = 'Pending type';
                  statusClass += ' text-[#2E6BAD]';
                }

                return (
                  <div
                    key={key}
                    className='relative min-w-0 max-w-full rounded-[16px] border-[1.5px] border-[#E2EBF3] bg-white px-5 py-5 has-[.doc-info-tip:hover]:z-40 has-[.doc-info-tip:focus-within]:z-40 sm:px-[22px]'
                  >
                    <div className='mb-2.5 flex min-w-0 items-start gap-2 text-sm font-semibold text-[#0B1929]'>
                      <span className='flex min-w-0 flex-1 items-center gap-x-[7px]'>
                        <span className='truncate'>{title}</span>
                        <span className='doc-info-tip group relative inline-flex shrink-0'>
                          <button
                            type='button'
                            className='inline-flex h-[18px] w-[18px] items-center justify-center rounded-full border border-[#B8C9D9] text-[11px] font-semibold leading-none text-[#7A9AB0] hover:border-[#2E6BAD] hover:text-[#2E6BAD]'
                            aria-label={`About ${title}`}
                          >
                            i
                          </button>
                          <span
                            role='tooltip'
                            className='pointer-events-none absolute left-0 top-[calc(100%+8px)] z-50 hidden w-[min(300px,calc(100vw-3rem))] rounded-[10px] bg-[#0B1929] px-3.5 py-3 text-left text-[12px] font-normal leading-[1.55] text-white shadow-lg group-hover:block group-focus-within:block'
                          >
                            {tooltip}
                          </span>
                        </span>
                      </span>
                      {statusLabel ? <span className={statusClass}>{statusLabel}</span> : null}
                    </div>

                    {recommendedHint ? <p className='mb-3 text-[12.5px] leading-5 text-[#4E6F8A]'>{recommendedHint}</p> : null}

                    {key === 'proofSourceOfFunds' ? (
                      <div className='mb-3.5 space-y-3.5'>
                        <div className='rounded-[12px] border border-[#DDE6EF] p-3.5'>
                          <label htmlFor='company-docs-sourceOfFundsDescription' className='text-[12px] font-semibold leading-5 text-[#3D5A73]'>
                            Describe the source of funds your company will use with us
                          </label>
                          <textarea
                            id='company-docs-sourceOfFundsDescription'
                            value={sourceOfFundsDescription}
                            disabled={isLocked}
                            onChange={(event) => {
                              setSourceOfFundsDescription(event.target.value);
                              setSofFieldErrors((prev) => {
                                const next = new Set(prev);
                                next.delete('sourceOfFundsDescription');
                                return next;
                              });
                            }}
                            placeholder='e.g. Operating revenue from B2B software subscriptions paid by EU customers.'
                            className={`${sofTextareaClass} mt-3`}
                            rows={3}
                          />
                          {sofFieldErrors.has('sourceOfFundsDescription') ? (
                            <p className={fieldErrorClass}>Please describe your source of funds.</p>
                          ) : null}
                        </div>
                      </div>
                    ) : null}

                    {isCovered ? (
                      <div className='mb-3.5 break-words rounded-[10px] border border-[#A8D5B5] bg-[#F3FAF5] px-3.5 py-3 text-[13px] leading-5 text-[#1F7A45]'>
                        Covered by your <span className='font-semibold'>{state.coveredByLabel}</span> upload
                        {state.coveredByFileName ? (
                          <>
                            {' '}
                            (<span className='font-medium'>{state.coveredByFileName}</span>)
                          </>
                        ) : null}
                        . You can still replace it with a dedicated document below.
                      </div>
                    ) : null}

                    {hasDocumentTypes ? (
                      <div className='relative mb-3.5 min-w-0'>
                        <label htmlFor={subtypeSelectId} className={fieldLabelClass}>
                          Document type <span className='text-[#C0392B]'>*</span>
                        </label>
                        <SearchableSelect
                          id={subtypeSelectId}
                          name={`documentSubtype-${key}`}
                          value={state.documentSubtype || ''}
                          onChange={(e) => handleCompanyDocumentSubtypeChange(key, e.target.value)}
                          placeholder='Select type'
                          options={documentTypes}
                          searchPlaceholder='Search document type...'
                          menuPlacement='down'
                          menuMaxHeight={240}
                          hideValue
                          disabled={isUploading || isLocked}
                          hasError={missingType}
                          triggerClassName={searchableSelectTriggerClass(missingType)}
                        />
                        {missingType ? (
                          <p className='mt-1.5 text-xs text-[#C0392B]'>Select a document type to finish uploading.</p>
                        ) : null}
                      </div>
                    ) : null}

                    <input
                      id={fileInputId}
                      type='file'
                      accept={COMPANY_DOC_ACCEPT}
                      className='sr-only'
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) handleCompanyFileSelect(key, file);
                        e.target.value = '';
                      }}
                      disabled={isUploading}
                    />

                    <div
                      role='button'
                      tabIndex={isUploading ? -1 : 0}
                      onClick={() => {
                        if (isUploading || isLocked) return;
                        document.getElementById(fileInputId)?.click();
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          if (isUploading || isLocked) return;
                          document.getElementById(fileInputId)?.click();
                        }
                      }}
                      onDragOver={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                      }}
                      onDrop={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        if (isUploading || isLocked) return;
                        const file = e.dataTransfer?.files?.[0];
                        if (file) handleCompanyFileSelect(key, file);
                      }}
                      className={`block min-w-0 max-w-full cursor-pointer rounded-[12px] border-[1.5px] border-dashed px-4 py-5 text-center transition ${uploadZoneClass}`}
                    >
                      {isDone ? (
                        <div className='grid min-w-0 grid-cols-1 gap-3 py-1 sm:grid-cols-[minmax(0,1fr)_auto] sm:items-center'>
                          <div className='flex min-w-0 items-center justify-center gap-2.5 text-sm font-semibold text-[#1F7A45] sm:justify-start'>
                            <svg width='18' height='18' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2.5' strokeLinecap='round' strokeLinejoin='round' className='shrink-0' aria-hidden='true'>
                              <polyline points='20 6 9 17 4 12' />
                            </svg>
                            <span className='min-w-0 truncate' title={displayName}>
                              {displayName}
                            </span>
                          </div>
                          <div className='flex shrink-0 items-center justify-center gap-3'>
                            {state.existingFileName ? (
                              <button
                                type='button'
                                className='text-xs font-medium text-[#2E6BAD] hover:underline'
                                onClick={async (e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  try {
                                    const url = await API.getDocumentAccessUrl(DocumentType.Compliance, state.existingFileName);
                                    if (url) window.open(url, '_blank', 'noopener,noreferrer');
                                  } catch {
                                    //
                                  }
                                }}
                              >
                                View
                              </button>
                            ) : null}
                            <button
                              type='button'
                              className='text-xs font-medium text-[#4E6F8A] hover:text-[#2E6BAD] hover:underline'
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                if (isUploading || isLocked) return;
                                document.getElementById(fileInputId)?.click();
                              }}
                            >
                              Replace
                            </button>
                          </div>
                        </div>
                      ) : (
                        <>
                          <div className='mx-auto mb-2.5 flex h-10 w-10 items-center justify-center rounded-[10px] bg-[#EBF4FB] text-[#4E6F8A]'>
                            <svg width='20' height='20' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='1.75' strokeLinecap='round' strokeLinejoin='round' aria-hidden='true'>
                              <path d='M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4' />
                              <polyline points='17 8 12 3 7 8' />
                              <line x1='12' y1='3' x2='12' y2='15' />
                            </svg>
                          </div>
                          <div className='text-[13.5px] text-[#3D5A73]'>
                            {isUploading ? (
                              'Uploading…'
                            ) : (
                              <>
                                <span className='font-semibold text-[#0B1929]'>{isCovered ? 'Click to replace' : 'Click to upload'}</span> or drag and drop
                              </>
                            )}
                          </div>
                          <div className='mt-1 text-xs text-[#7A9AB0]'>PDF, JPG or PNG · Max 10 MB</div>
                        </>
                      )}
                    </div>

                    {state.error ? <p className='mt-2 text-xs text-[#C0392B]'>{state.error}</p> : null}
                  </div>
                );
              })}

              <div className='rounded-[12px] bg-[#EBF4FB] px-4 py-3.5 text-[13px] leading-[1.6] text-[#4E6F8A]'>
                <svg
                  width='14'
                  height='14'
                  viewBox='0 0 24 24'
                  fill='none'
                  stroke='#7A9AB0'
                  strokeWidth='2'
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  className='mr-1.5 inline align-[-2px]'
                  aria-hidden='true'
                >
                  <circle cx='12' cy='12' r='10' />
                  <line x1='12' y1='8' x2='12' y2='12' />
                  <line x1='12' y1='16' x2='12.01' y2='16' />
                </svg>
                All documents are processed securely and stored encrypted in compliance with GDPR.
              </div>

            </fieldset>
          </div>
        </div>
      </div>
    );
  }

  if (showSummary && displayCompany) {
    const company = typeof displayCompany === 'object' && displayCompany !== null ? displayCompany : {};
    const regAddr = company.registeredAddress || company.registered_address;
    const opAddr = company.operatingAddress || company.operating_address;
    const formatAddress = (addr) =>
      addr ? [addr.line1, addr.city, addr.stateRegionProvince, addr.postCode, addr.country].filter(Boolean).join(', ') : '';
    const registeredAddressDisplay = formatAddress(regAddr) || '—';
    const operatingFormatted = formatAddress(opAddr);
    const operatingAddressDisplay =
      !operatingFormatted || operatingFormatted === registeredAddressDisplay ? 'Same as registered' : operatingFormatted;
    const registrationDateDisplay =
      typeof company.registrationDate === 'string' ? company.registrationDate.slice(0, 10) : company.registrationDate || '—';
    const tradeName = String(company.tradeName ?? '').trim();
    const legalFormValue = resolveLegalFormValue(company.legalForm);
    const legalFormLabel =
      LEGAL_FORM_OPTIONS.find((opt) => opt.value === legalFormValue)?.label || company.legalForm || '—';
    const onboardingProfile = company.onboardingProfile || company.OnboardingProfile || {};
    const accountPurpose =
      onboardingProfile.accountPurpose ?? onboardingProfile.AccountPurpose ?? company.accountPurpose ?? '';
    const sourceOfFunds =
      onboardingProfile.sourceOfFunds ?? onboardingProfile.SourceOfFunds ?? company.sourceOfFunds ?? '';
    const accountPurposeOther = String(
      onboardingProfile.accountPurposeOther ??
        onboardingProfile.AccountPurposeOther ??
        company.accountPurposeOther ??
        company.account_purpose_other ??
        '',
    ).trim();
    const sourceOfFundsOther = String(
      onboardingProfile.sourceOfFundsOther ??
        onboardingProfile.SourceOfFundsOther ??
        company.sourceOfFundsOther ??
        company.source_of_funds_other ??
        '',
    ).trim();
    const accountPurposeBase = ACCOUNT_PURPOSE_OPTIONS.find((opt) => opt.value === accountPurpose)?.label || accountPurpose || '—';
    const sourceOfFundsBase = SOURCE_OF_FUNDS_OPTIONS.find((opt) => opt.value === sourceOfFunds)?.label || sourceOfFunds || '—';
    const accountPurposeLabel =
      accountPurpose === 'Other' && accountPurposeOther ? `${accountPurposeBase}: ${accountPurposeOther}` : accountPurposeBase;
    const sourceOfFundsLabel =
      sourceOfFunds === 'Other' && sourceOfFundsOther ? `${sourceOfFundsBase}: ${sourceOfFundsOther}` : sourceOfFundsBase;
    const naceDisplay = String(company.industryNaces ?? '')
      .split(',')
      .map((code) => code.trim())
      .filter(Boolean)
      .map((code) => {
        const match = industryNaceOptions.find((opt) => opt.code === code);
        return match ? `${match.code} – ${match.description}` : code;
      })
      .join(', ') || '—';

    const handleEditCompanyDetails = () => {
      setShowSummary(false);
      setShowCompanyFiles(false);
      setShowCompanyDetailsScreen(true);
    };

    return (
      <ReviewStepShell
        icon={<DocumentReviewIcon />}
        title='Review Company Details.'
        subtitle='Please confirm before we continue.'
        footer={
          advanceOnboardingStepError ? (
            <ErrorMessage
              error={advanceOnboardingStepError?.message ?? advanceOnboardingStepError?.detail ?? 'Could not advance. Please try again.'}
              className='mt-4'
            />
          ) : null
        }
      >
        <ReviewSection title='Company' onEdit={handleEditCompanyDetails} editDisabled={isLocked}>
          <ReviewRow label='Registered name' value={company.registeredName ?? company.legalName ?? company.name} />
          {tradeName ? <ReviewRow label='Trade name' value={tradeName} /> : null}
          <ReviewRow label='Legal form' value={legalFormLabel} />
          <ReviewRow label='Registration no.' value={company.registrationNumber ?? company.organizationNumber} />
          <ReviewRow label='Registration date' value={registrationDateDisplay} />
          <ReviewRow label='Tax Identification Number (VAT)' value={company.vatNumber} />
        </ReviewSection>

        <ReviewSection title='Industry' onEdit={handleEditCompanyDetails} editDisabled={isLocked}>
          <ReviewRow label='NACE codes' value={naceDisplay} />
          <ReviewRow label='Description' value={company.description} />
        </ReviewSection>

        <ReviewSection title='Contact & account' onEdit={handleEditCompanyDetails} editDisabled={isLocked}>
          <ReviewRow label='Email' value={company.email} />
          <ReviewRow label='Phone' value={company.phoneNumber} />
          <ReviewRow label='Website' value={company.website} />
          <ReviewRow label='Account purpose' value={accountPurposeLabel} />
          <ReviewRow label='Source of funds' value={sourceOfFundsLabel} />
        </ReviewSection>

        <ReviewSection title='Address' isLast onEdit={handleEditCompanyDetails} editDisabled={isLocked}>
          <ReviewRow label='Registered' value={registeredAddressDisplay} />
          <ReviewRow label='Operating' value={operatingAddressDisplay || 'Same as registered'} />
        </ReviewSection>
      </ReviewStepShell>
    );
  }


  if (showCompanyDetailsScreen && companyForm) {
    const descriptionLength = String(companyForm.description ?? '').trim().length;
    const legalFormValue = resolveLegalFormValue(companyForm.legalForm);
    const legalFormOptions =
      legalFormValue && !LEGAL_FORM_OPTIONS.some((opt) => opt.value === legalFormValue)
        ? [...LEGAL_FORM_OPTIONS, { value: legalFormValue, label: legalFormValue }]
        : LEGAL_FORM_OPTIONS;
    const registeredAddress = companyForm.registeredAddress || {};

    return (
      <div className='flex w-full justify-center bg-[#EBF4FB] px-4 pt-7 font-sans text-[#0B1929] sm:min-h-screen sm:px-6'>
        <div className='w-full max-w-[540px] sm:mb-24'>
          <div ref={companyDetailsSectionRef} className='rounded-[20px] bg-white px-6 py-8 shadow-[0_4px_32px_rgba(11,25,41,0.08)] sm:px-10 sm:py-9'>
            <div className='mx-auto mb-5 flex h-14 w-14 items-center justify-center rounded-full bg-[#EBF4FB] text-[#1E4E80]'>
              <BuildingIcon className='h-[26px] w-[26px]' />
            </div>
            <h1 className='mb-2.5 text-center font-serif text-2xl font-normal leading-[1.25] text-[#0B1929]'>Company Information.</h1>
            <p className='mt-2 text-center text-sm leading-6 text-[#4E6F8A]'>Review and complete your company&apos;s details.</p>

            {updateUserError && (
              <div className='mt-5'>
                <ErrorMessage error={updateUserError?.message ?? updateUserError?.detail ?? 'Failed to save company.'} />
              </div>
            )}

            <fieldset disabled={isLocked} className='block border-0 p-0'>
              <SectionHeader
                icon={
                  <svg width='13' height='13' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2.5' strokeLinecap='round' strokeLinejoin='round' aria-hidden='true'>
                    <rect x='2' y='7' width='20' height='14' rx='1' />
                    <path d='M16 7V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v2' />
                  </svg>
                }
              >
                Company details
              </SectionHeader>

              <div className={fieldGroupClass}>
                <label htmlFor='company-registeredName' className={fieldLabelClass}>
                  Registered name
                </label>
                <input
                  id='company-registeredName'
                  type='text'
                  className={`${inputClass} ${companyFormFieldErrors.has('registeredName') ? inputErrorBorderClass : ''}`}
                  value={companyForm.registeredName ?? ''}
                  onChange={(e) => {
                    setCompanyForm((prev) => ({ ...(prev || {}), registeredName: e.target.value }));
                    setCompanyFormFieldErrors((prev) => {
                      const next = new Set(prev);
                      next.delete('registeredName');
                      return next;
                    });
                  }}
                />
                {companyFormFieldErrors.has('registeredName') && <p className={fieldErrorClass}>{getCompanyFormFieldErrorMessage('registeredName')}</p>}
              </div>

              <div className={fieldGroupClass}>
                <label htmlFor='company-tradeName' className={fieldLabelClass}>
                  Trade name <span className='font-normal opacity-60'>(optional)</span>
                </label>
                <input
                  id='company-tradeName'
                  type='text'
                  className={inputClass}
                  value={companyForm.tradeName ?? ''}
                  onChange={(e) => setCompanyForm((prev) => ({ ...(prev || {}), tradeName: e.target.value }))}
                  placeholder='If different from registered name'
                />
              </div>

              <div className='grid grid-cols-1 gap-0 sm:grid-cols-2 sm:gap-3'>
                <div className={fieldGroupClass}>
                  <label htmlFor='company-legalForm' className={fieldLabelClass}>
                    Legal form
                  </label>
                  <SearchableSelect
                    id='company-legalForm'
                    name='legalForm'
                    value={legalFormValue}
                    onChange={(e) => {
                      setCompanyForm((prev) => ({ ...(prev || {}), legalForm: e.target.value }));
                      setCompanyFormFieldErrors((prev) => {
                        const next = new Set(prev);
                        next.delete('legalForm');
                        return next;
                      });
                    }}
                    placeholder='Select...'
                    options={legalFormOptions}
                    searchPlaceholder='Search legal form...'
                    menuPlacement='auto'
                    menuMinWidth='w-full'
                    clearable
                    hideValue
                    hasError={companyFormFieldErrors.has('legalForm')}
                    triggerClassName={searchableSelectTriggerClass(companyFormFieldErrors.has('legalForm'))}
                  />
                  {companyFormFieldErrors.has('legalForm') && <p className={fieldErrorClass}>{getCompanyFormFieldErrorMessage('legalForm')}</p>}
                </div>
                <div className={fieldGroupClass}>
                  <label htmlFor='company-registrationNumber' className={fieldLabelClass}>
                    Registration number
                  </label>
                  <input
                    id='company-registrationNumber'
                    type='text'
                    className={`${inputClass} ${companyFormFieldErrors.has('registrationNumber') ? inputErrorBorderClass : ''}`}
                    value={companyForm.registrationNumber ?? ''}
                    placeholder='e.g. HRB 239847 B'
                    onChange={(e) => {
                      setCompanyForm((prev) => ({ ...(prev || {}), registrationNumber: e.target.value }));
                      setCompanyFormFieldErrors((prev) => {
                        const next = new Set(prev);
                        next.delete('registrationNumber');
                        return next;
                      });
                    }}
                  />
                  {companyFormFieldErrors.has('registrationNumber') && (
                    <p className={fieldErrorClass}>{getCompanyFormFieldErrorMessage('registrationNumber')}</p>
                  )}
                </div>
              </div>

              <div className={fieldGroupClass}>
                <label htmlFor='company-registrationDate' className={fieldLabelClass}>
                  Registration date
                </label>
                <DatePicker
                  id='company-registrationDate'
                  name='registrationDate'
                  className={`${inputClass} ${companyFormFieldErrors.has('registrationDate') ? inputErrorBorderClass : ''}`}
                  value={companyForm.registrationDate ?? ''}
                  onChange={(e) => {
                    setCompanyForm((prev) => ({ ...(prev || {}), registrationDate: e.target.value }));
                    setCompanyFormFieldErrors((prev) => {
                      const next = new Set(prev);
                      next.delete('registrationDate');
                      return next;
                    });
                  }}
                />
                {companyFormFieldErrors.has('registrationDate') && (
                  <p className={fieldErrorClass}>{getCompanyFormFieldErrorMessage('registrationDate')}</p>
                )}
              </div>

              <div className={fieldGroupClass}>
                <label htmlFor='company-vatNumber' className={fieldLabelClass}>
                  Tax Identification Number (VAT)
                </label>
                <input
                  id='company-vatNumber'
                  type='text'
                  className={`${inputClass} ${companyFormFieldErrors.has('vatNumber') ? inputErrorBorderClass : ''}`}
                  value={companyForm.vatNumber ?? ''}
                  onChange={(e) => {
                    setCompanyForm((prev) => ({ ...(prev || {}), vatNumber: e.target.value }));
                    setCompanyFormFieldErrors((prev) => {
                      const next = new Set(prev);
                      next.delete('vatNumber');
                      return next;
                    });
                  }}
                />
                {companyFormFieldErrors.has('vatNumber') && <p className={fieldErrorClass}>{getCompanyFormFieldErrorMessage('vatNumber')}</p>}
              </div>

              <SectionHeader
                icon={
                  <svg width='13' height='13' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2.5' strokeLinecap='round' strokeLinejoin='round' aria-hidden='true'>
                    <polyline points='22 12 18 12 15 21 9 3 6 12 2 12' />
                  </svg>
                }
              >
                Industry & activity
              </SectionHeader>

              <div className={fieldGroupClass}>
                <SearchableIndustryNaceSelect
                  id='company-industryNaces'
                  value={companyForm.industryNaces ?? ''}
                  hasError={companyFormFieldErrors.has('industryNaces')}
                  onChange={(newValue) => {
                    setCompanyForm((prev) => ({ ...(prev || {}), industryNaces: newValue || '' }));
                    setCompanyFormFieldErrors((prev) => {
                      const next = new Set(prev);
                      next.delete('industryNaces');
                      return next;
                    });
                  }}
                />
                {companyFormFieldErrors.has('industryNaces') && (
                  <p className={fieldErrorClass}>{getCompanyFormFieldErrorMessage('industryNaces')}</p>
                )}
              </div>

              <div className={fieldGroupClass}>
                <label htmlFor='company-description' className={fieldLabelClass}>
                  Business description
                </label>
                <textarea
                  id='company-description'
                  rows={4}
                  className={`${textareaClass} ${companyFormFieldErrors.has('description') ? inputErrorBorderClass : ''}`}
                  value={companyForm.description ?? ''}
                  placeholder='Briefly describe what your company does, what you sell, and who your customers are.'
                  onChange={(e) => {
                    setCompanyForm((prev) => ({ ...(prev || {}), description: e.target.value }));
                    setCompanyFormFieldErrors((prev) => {
                      const next = new Set(prev);
                      next.delete('description');
                      return next;
                    });
                  }}
                />
                <div className='mt-1.5 flex items-center justify-between'>
                  <span className='text-xs text-[#4E6F8A]'>Minimum 50 characters</span>
                  <span className={`text-xs transition-colors ${descriptionLength >= 50 ? 'text-[#1F8A4C]' : 'text-[#4E6F8A]'}`}>
                    {descriptionLength} characters
                  </span>
                </div>
                {companyFormFieldErrors.has('description') && (
                  <p className={fieldErrorClass}>{getCompanyFormFieldErrorMessage('description')}</p>
                )}
              </div>

              <SectionHeader
                icon={
                  <svg width='13' height='13' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2.5' strokeLinecap='round' strokeLinejoin='round' aria-hidden='true'>
                    <path d='M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.11 9.14 19.79 19.79 0 0 1 1 .5 2 2 0 0 1 2.82.34h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91' />
                  </svg>
                }
              >
                Contact
              </SectionHeader>

              <div className={fieldGroupClass}>
                <label htmlFor='company-email' className={fieldLabelClass}>
                  Company email
                </label>
                <input
                  id='company-email'
                  type='email'
                  className={`${inputClass} ${companyFormFieldErrors.has('email') ? inputErrorBorderClass : ''}`}
                  value={companyForm.email ?? ''}
                  placeholder='info@company.com'
                  onChange={(e) => {
                    setCompanyForm((prev) => ({ ...(prev || {}), email: e.target.value }));
                    setCompanyFormFieldErrors((prev) => {
                      const next = new Set(prev);
                      next.delete('email');
                      return next;
                    });
                  }}
                />
                {companyFormFieldErrors.has('email') && <p className={fieldErrorClass}>{getCompanyFormFieldErrorMessage('email')}</p>}
              </div>

              <div className={fieldGroupClass}>
                <label htmlFor='company-phoneNumber' className={fieldLabelClass}>
                  Phone number
                </label>
                <PhoneNumberInput
                  id='company-phoneNumber'
                  value={companyForm.phoneNumber ?? ''}
                  onChange={(nextValue) => {
                    setCompanyForm((prev) => ({ ...(prev || {}), phoneNumber: nextValue }));
                    setCompanyFormFieldErrors((prev) => {
                      const next = new Set(prev);
                      next.delete('phoneNumber');
                      return next;
                    });
                  }}
                  defaultCountryCode={(
                    companyForm?.registeredAddress?.country ||
                    country ||
                    ''
                  ).toUpperCase()}
                  allowedCountries={(allowedOnboardingCountries || []).map((c) => (c.code || '').toUpperCase()).filter(Boolean)}
                  inputClassName={`${inputClass} ${companyFormFieldErrors.has('phoneNumber') ? inputErrorBorderClass : ''}`}
                  selectClassName={`${inputClass} w-[130px] min-w-[130px] pr-8 ${companyFormFieldErrors.has('phoneNumber') ? inputErrorBorderClass : ''}`}
                  placeholder='e.g. 1234 567 890'
                />
                {companyFormFieldErrors.has('phoneNumber') && (
                  <p className={fieldErrorClass}>{getCompanyFormFieldErrorMessage('phoneNumber')}</p>
                )}
              </div>

              <div className={fieldGroupClass}>
                <label htmlFor='company-website' className={fieldLabelClass}>
                  Website
                </label>
                <input
                  id='company-website'
                  type='text'
                  className={`${inputClass} ${companyFormFieldErrors.has('website') ? inputErrorBorderClass : ''}`}
                  value={companyForm.website ?? ''}
                  placeholder='https://yourcompany.com'
                  onChange={(e) => {
                    setCompanyForm((prev) => ({ ...(prev || {}), website: e.target.value }));
                    setCompanyFormFieldErrors((prev) => {
                      const next = new Set(prev);
                      next.delete('website');
                      return next;
                    });
                  }}
                />
                {companyFormFieldErrors.has('website') && <p className={fieldErrorClass}>{getCompanyFormFieldErrorMessage('website')}</p>}
              </div>

              <SectionHeader
                icon={
                  <svg width='13' height='13' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2.5' strokeLinecap='round' strokeLinejoin='round' aria-hidden='true'>
                    <rect x='1' y='4' width='22' height='16' rx='2' ry='2' />
                    <line x1='1' y1='10' x2='23' y2='10' />
                  </svg>
                }
              >
                Account & funds
              </SectionHeader>

              <div className={fieldGroupClass}>
                <label htmlFor='company-accountPurpose' className={fieldLabelClass}>
                  Account purpose
                </label>
                <SearchableSelect
                  id='company-accountPurpose'
                  name='accountPurpose'
                  value={companyForm.accountPurpose ?? ''}
                  onChange={(e) => {
                    const accountPurpose = e.target.value;
                    setCompanyForm((prev) => ({
                      ...(prev || {}),
                      accountPurpose,
                      accountPurposeOther: accountPurpose === 'Other' ? prev?.accountPurposeOther ?? '' : '',
                    }));
                    setCompanyFormFieldErrors((prev) => {
                      const next = new Set(prev);
                      next.delete('accountPurpose');
                      if (accountPurpose !== 'Other') next.delete('accountPurposeOther');
                      return next;
                    });
                  }}
                  placeholder='Select...'
                  options={ACCOUNT_PURPOSE_OPTIONS}
                  searchPlaceholder='Search account purpose...'
                  menuPlacement='auto'
                  menuMinWidth='w-full'
                  clearable
                  hideValue
                  hasError={companyFormFieldErrors.has('accountPurpose')}
                  triggerClassName={searchableSelectTriggerClass(companyFormFieldErrors.has('accountPurpose'))}
                />
                {companyFormFieldErrors.has('accountPurpose') && (
                  <p className={fieldErrorClass}>{getCompanyFormFieldErrorMessage('accountPurpose')}</p>
                )}
              </div>
              {companyForm.accountPurpose === 'Other' && (
                <div className={`${fieldGroupClass} -mt-2`}>
                  <label htmlFor='company-accountPurposeOther' className={fieldLabelClass}>
                    Please explain the account purpose
                  </label>
                  <input
                    id='company-accountPurposeOther'
                    type='text'
                    className={`${inputClass} ${companyFormFieldErrors.has('accountPurposeOther') ? inputErrorBorderClass : ''}`}
                    value={companyForm.accountPurposeOther ?? ''}
                    onChange={(e) => {
                      setCompanyForm((prev) => ({ ...(prev || {}), accountPurposeOther: e.target.value }));
                      setCompanyFormFieldErrors((prev) => {
                        const next = new Set(prev);
                        next.delete('accountPurposeOther');
                        return next;
                      });
                    }}
                  />
                  {companyFormFieldErrors.has('accountPurposeOther') && (
                    <p className={fieldErrorClass}>{getCompanyFormFieldErrorMessage('accountPurposeOther')}</p>
                  )}
                </div>
              )}

              <div className={fieldGroupClass}>
                <label htmlFor='company-sourceOfFunds' className={fieldLabelClass}>
                  Main source of funds
                </label>
                <SearchableSelect
                  id='company-sourceOfFunds'
                  name='sourceOfFunds'
                  value={companyForm.sourceOfFunds ?? ''}
                  onChange={(e) => {
                    const sourceOfFunds = e.target.value;
                    setCompanyForm((prev) => ({
                      ...(prev || {}),
                      sourceOfFunds,
                      sourceOfFundsOther: sourceOfFunds === 'Other' ? prev?.sourceOfFundsOther ?? '' : '',
                    }));
                    setCompanyFormFieldErrors((prev) => {
                      const next = new Set(prev);
                      next.delete('sourceOfFunds');
                      if (sourceOfFunds !== 'Other') next.delete('sourceOfFundsOther');
                      return next;
                    });
                  }}
                  placeholder='Select...'
                  options={SOURCE_OF_FUNDS_OPTIONS}
                  searchPlaceholder='Search source of funds...'
                  menuPlacement='auto'
                  menuMinWidth='w-full'
                  clearable
                  hideValue
                  hasError={companyFormFieldErrors.has('sourceOfFunds')}
                  triggerClassName={searchableSelectTriggerClass(companyFormFieldErrors.has('sourceOfFunds'))}
                />
                {companyFormFieldErrors.has('sourceOfFunds') && (
                  <p className={fieldErrorClass}>{getCompanyFormFieldErrorMessage('sourceOfFunds')}</p>
                )}
              </div>
              {companyForm.sourceOfFunds === 'Other' && (
                <div className={`${fieldGroupClass} -mt-2`}>
                  <label htmlFor='company-sourceOfFundsOther' className={fieldLabelClass}>
                    Please explain the source of funds
                  </label>
                  <input
                    id='company-sourceOfFundsOther'
                    type='text'
                    className={`${inputClass} ${companyFormFieldErrors.has('sourceOfFundsOther') ? inputErrorBorderClass : ''}`}
                    value={companyForm.sourceOfFundsOther ?? ''}
                    onChange={(e) => {
                      setCompanyForm((prev) => ({ ...(prev || {}), sourceOfFundsOther: e.target.value }));
                      setCompanyFormFieldErrors((prev) => {
                        const next = new Set(prev);
                        next.delete('sourceOfFundsOther');
                        return next;
                      });
                    }}
                  />
                  {companyFormFieldErrors.has('sourceOfFundsOther') && (
                    <p className={fieldErrorClass}>{getCompanyFormFieldErrorMessage('sourceOfFundsOther')}</p>
                  )}
                </div>
              )}

              <SectionHeader
                icon={
                  <svg width='13' height='13' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2.5' strokeLinecap='round' strokeLinejoin='round' aria-hidden='true'>
                    <path d='M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z' />
                    <circle cx='12' cy='10' r='3' />
                  </svg>
                }
              >
                Registered address
              </SectionHeader>

              <div className={fieldGroupClass}>
                <label htmlFor='reg-addr-line1' className={fieldLabelClass}>
                  Address line 1
                </label>
                <input
                  id='reg-addr-line1'
                  type='text'
                  className={`${inputClass} ${regAddrFieldErrors.has('line1') ? inputErrorBorderClass : ''}`}
                  value={registeredAddress.line1 ?? ''}
                  onChange={(e) => {
                    setCompanyForm((prev) => ({
                      ...(prev || {}),
                      registeredAddress: { ...(prev?.registeredAddress || {}), line1: e.target.value },
                    }));
                    setRegAddrFieldErrors((prev) => {
                      const next = new Set(prev);
                      next.delete('line1');
                      return next;
                    });
                  }}
                />
                {regAddrFieldErrors.has('line1') && <p className={fieldErrorClass}>Required</p>}
              </div>

              <div className={fieldGroupClass}>
                <label htmlFor='reg-addr-line2' className={fieldLabelClass}>
                  Address line 2 <span className='font-normal normal-case opacity-60'>(optional)</span>
                </label>
                <input
                  id='reg-addr-line2'
                  type='text'
                  className={inputClass}
                  value={registeredAddress.line2 ?? ''}
                  placeholder='Floor, suite, etc.'
                  onChange={(e) =>
                    setCompanyForm((prev) => ({
                      ...(prev || {}),
                      registeredAddress: { ...(prev?.registeredAddress || {}), line2: e.target.value },
                    }))
                  }
                />
              </div>

              <div className='grid grid-cols-1 gap-0 sm:grid-cols-2 sm:gap-3'>
                <div className={fieldGroupClass}>
                  <label htmlFor='reg-addr-city' className={fieldLabelClass}>
                    City
                  </label>
                  <input
                    id='reg-addr-city'
                    type='text'
                    className={`${inputClass} ${regAddrFieldErrors.has('city') ? inputErrorBorderClass : ''}`}
                    value={registeredAddress.city ?? ''}
                    onChange={(e) => {
                      setCompanyForm((prev) => ({
                        ...(prev || {}),
                        registeredAddress: { ...(prev?.registeredAddress || {}), city: e.target.value },
                      }));
                      setRegAddrFieldErrors((prev) => {
                        const next = new Set(prev);
                        next.delete('city');
                        return next;
                      });
                    }}
                  />
                  {regAddrFieldErrors.has('city') && <p className={fieldErrorClass}>Required</p>}
                </div>
                <div className={fieldGroupClass}>
                  <label htmlFor='reg-addr-state' className={fieldLabelClass}>
                    State / Region / Province
                  </label>
                  <input
                    id='reg-addr-state'
                    type='text'
                    className={`${inputClass} ${regAddrFieldErrors.has('stateRegionProvince') ? inputErrorBorderClass : ''}`}
                    value={registeredAddress.stateRegionProvince ?? ''}
                    onChange={(e) => {
                      setCompanyForm((prev) => ({
                        ...(prev || {}),
                        registeredAddress: { ...(prev?.registeredAddress || {}), stateRegionProvince: e.target.value },
                      }));
                      setRegAddrFieldErrors((prev) => {
                        const next = new Set(prev);
                        next.delete('stateRegionProvince');
                        return next;
                      });
                    }}
                  />
                  {regAddrFieldErrors.has('stateRegionProvince') && <p className={fieldErrorClass}>Required</p>}
                </div>
              </div>

              <div className='grid grid-cols-1 gap-0 sm:grid-cols-2 sm:gap-3'>
                <div className={fieldGroupClass}>
                  <label htmlFor='reg-addr-postCode' className={fieldLabelClass}>
                    Post code
                  </label>
                  <input
                    id='reg-addr-postCode'
                    type='text'
                    className={`${inputClass} ${regAddrFieldErrors.has('postCode') ? inputErrorBorderClass : ''}`}
                    value={registeredAddress.postCode ?? ''}
                    onChange={(e) => {
                      setCompanyForm((prev) => ({
                        ...(prev || {}),
                        registeredAddress: { ...(prev?.registeredAddress || {}), postCode: e.target.value },
                      }));
                      setRegAddrFieldErrors((prev) => {
                        const next = new Set(prev);
                        next.delete('postCode');
                        return next;
                      });
                    }}
                  />
                  {regAddrFieldErrors.has('postCode') && <p className={fieldErrorClass}>Required</p>}
                </div>
                <div className={fieldGroupClass}>
                  <label htmlFor='reg-addr-country' className={fieldLabelClass}>
                    Country
                  </label>
                  <SupportedCountrySelect
                    id='reg-addr-country'
                    className={searchableSelectTriggerClass(regAddrFieldErrors.has('country'))}
                    value={registeredAddress.country ?? ''}
                    hasError={regAddrFieldErrors.has('country')}
                    onChange={(e) => {
                      setCompanyForm((prev) => ({
                        ...(prev || {}),
                        registeredAddress: { ...(prev?.registeredAddress || {}), country: e.target.value.toUpperCase() },
                      }));
                      setRegAddrFieldErrors((prev) => {
                        const next = new Set(prev);
                        next.delete('country');
                        return next;
                      });
                    }}
                  />
                  {regAddrFieldErrors.has('country') && <p className={fieldErrorClass}>Required</p>}
                </div>
              </div>

              <div className='mb-4 mt-1 flex items-center gap-2.5 rounded-[12px] bg-[#EBF4FB] px-4 py-3.5'>
                <input
                  id='operating-differs'
                  type='checkbox'
                  checked={operatingAddressDiffers}
                  onChange={(e) => {
                    const { checked } = e.target;
                    setOperatingAddressDiffers(checked);
                    setOperatingAddressError(null);
                    if (!checked) setOperatingAddressSearch('');
                  }}
                  className='h-4 w-4 shrink-0 cursor-pointer accent-[#1E4E80]'
                />
                <label htmlFor='operating-differs' className='mb-0 cursor-pointer text-[13.5px] font-normal tracking-normal text-[#3D5A73]'>
                  The operating address differs from the registered address
                </label>
              </div>

              {operatingAddressDiffers && (
                <div>
                  <div className={`${sectionHeaderClass} mt-0`}>Operating address</div>
                  <div className={`${fieldGroupClass} md:col-span-2`}>
                    <label htmlFor='op-addr-search' className={fieldLabelClass}>
                      Address search
                    </label>
                    <NewAddressAutocomplete
                      id='op-addr-search'
                      value={operatingAddressSearch}
                      onChange={(v) => setOperatingAddressSearch(v)}
                      onAddressSelect={handleOperatingAddressSearchSelect}
                      placeholder='Search for an address'
                      className='w-full'
                      allowedCountries={
                        manualOperatingAddress.country ? [manualOperatingAddress.country] : []
                      }
                      includedPrimaryTypes={['street_address', 'route', 'premise', 'subpremise']}
                    />
                  </div>
                  <div className={fieldGroupClass}>
                    <label htmlFor='op-addr-line1' className={fieldLabelClass}>
                      Address line 1
                    </label>
                    <input
                      id='op-addr-line1'
                      type='text'
                      className={inputClass}
                      value={manualOperatingAddress.line1}
                      onChange={(e) => setManualOperatingAddress((prev) => ({ ...prev, line1: e.target.value }))}
                    />
                  </div>
                  <div className={fieldGroupClass}>
                    <label htmlFor='op-addr-line2' className={fieldLabelClass}>
                      Address line 2 <span className='font-normal normal-case opacity-60'>(optional)</span>
                    </label>
                    <input
                      id='op-addr-line2'
                      type='text'
                      className={inputClass}
                      value={manualOperatingAddress.line2}
                      onChange={(e) => setManualOperatingAddress((prev) => ({ ...prev, line2: e.target.value }))}
                    />
                  </div>
                  <div className='grid grid-cols-1 gap-0 sm:grid-cols-2 sm:gap-3'>
                    <div className={fieldGroupClass}>
                      <label htmlFor='op-addr-city' className={fieldLabelClass}>
                        City
                      </label>
                      <input
                        id='op-addr-city'
                        type='text'
                        className={inputClass}
                        value={manualOperatingAddress.city}
                        onChange={(e) => setManualOperatingAddress((prev) => ({ ...prev, city: e.target.value }))}
                      />
                    </div>
                    <div className={fieldGroupClass}>
                      <label htmlFor='op-addr-state' className={fieldLabelClass}>
                        State / Region / Province
                      </label>
                      <input
                        id='op-addr-state'
                        type='text'
                        className={inputClass}
                        value={manualOperatingAddress.stateRegionProvince}
                        onChange={(e) => {
                          setManualOperatingAddress((prev) => ({ ...prev, stateRegionProvince: e.target.value }));
                          setOperatingAddressError(null);
                        }}
                      />
                    </div>
                  </div>
                  <div className='grid grid-cols-1 gap-0 sm:grid-cols-2 sm:gap-3'>
                    <div className={fieldGroupClass}>
                      <label htmlFor='op-addr-postCode' className={fieldLabelClass}>
                        Post code
                      </label>
                      <input
                        id='op-addr-postCode'
                        type='text'
                        className={inputClass}
                        value={manualOperatingAddress.postCode}
                        onChange={(e) => setManualOperatingAddress((prev) => ({ ...prev, postCode: e.target.value }))}
                      />
                    </div>
                    <div className={fieldGroupClass}>
                      <label htmlFor='op-addr-country' className={fieldLabelClass}>
                        Country
                      </label>
                      <SupportedCountrySelect
                        id='op-addr-country'
                        className={searchableSelectTriggerClass()}
                        value={manualOperatingAddress.country}
                        onChange={(e) => setManualOperatingAddress((prev) => ({ ...prev, country: e.target.value.toUpperCase() }))}
                      />
                    </div>
                  </div>
                  {operatingAddressError && <p className={fieldErrorClass}>{operatingAddressError}</p>}
                </div>
              )}

            </fieldset>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className='flex w-full justify-center bg-[#EBF4FB] px-4 pt-7 font-sans text-[#0B1929] sm:min-h-screen sm:px-6'>
      <div className='w-full max-w-[540px] space-y-6 sm:mb-24'>
        <div className='rounded-[20px] bg-white px-6 py-8 shadow-[0_4px_32px_rgba(11,25,41,0.08)] sm:px-10 sm:py-9'>
          <div className='mx-auto mb-5 flex h-14 w-14 items-center justify-center rounded-full bg-[#EBF4FB] text-[#1E4E80]'>
            <BuildingIcon className='h-[26px] w-[26px]' />
          </div>
          <h1 className='mb-2.5 text-center font-serif text-2xl font-normal leading-[1.25] text-[#0B1929]'>Company Account Setup.</h1>
          <p className='mt-2 text-center text-sm leading-6 text-[#4E6F8A]'>We&apos;ll pre-fill your details from the commercial registry.</p>

          <div className='mt-7 rounded-[16px] border-[1.5px] border-[#DDE6EF] bg-[#F8FAFB] px-5 py-6 sm:px-[26px]'>
            <div className='mb-1 text-sm font-semibold text-[#0B1929]'>Company lookup</div>
            <div className='mb-[18px] text-[13px] text-[#4E6F8A]'>Search using your legal name or registration number</div>

            <fieldset disabled={isLocked}>
              <form onSubmit={handleSearch}>
                <div className={fieldGroupClass}>
                  <label htmlFor='companyName' className={fieldLabelClass}>
                    Company name
                  </label>
                  <input
                    id='companyName'
                    type='text'
                    value={companyName}
                    onChange={(e) => setCompanyName(e.target.value)}
                    placeholder='e.g. HansePay GmbH'
                    className={inputClass}
                    disabled={searching}
                  />
                </div>

                <div className={fieldGroupClass}>
                  <label htmlFor='country' className={fieldLabelClass}>
                    Country of registration
                  </label>
                  <SearchableSelect
                    id='country'
                    name='country'
                    value={country}
                    onChange={(e) => setCountry(e.target.value)}
                    placeholder={countryOptions.length === 0 ? 'Loading countries...' : 'Search country...'}
                    options={countryOptions}
                    showCountryFlags
                    searchPlaceholder='Search country...'
                    menuPlacement='auto'
                    menuMaxHeight={240}
                    menuMinWidth='w-full'
                    clearable={false}
                    hideValue
                    disabled={searching || countryOptions.length === 0}
                    triggerClassName={searchableSelectTriggerClass()}
                  />
                </div>

                <button type='submit' disabled={searching || !companyName?.trim()} className={`${primaryButtonClass} mt-1`}>
                  {searching ? (
                    'Searching…'
                  ) : (
                    <>
                      <svg width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2.5' strokeLinecap='round' strokeLinejoin='round' aria-hidden='true'>
                        <circle cx='11' cy='11' r='8' />
                        <line x1='21' y1='21' x2='16.65' y2='16.65' />
                      </svg>
                      Search
                    </>
                  )}
                </button>
              </form>
            </fieldset>

            {searching && (
              <div className='py-[22px] text-center'>
                <div className='mx-auto mb-2.5 h-6 w-6 animate-spin rounded-full border-[2.5px] border-[#DDE6EF] border-t-[#1E4E80]' />
                <div className='text-[13px] text-[#4E6F8A]'>Searching registry…</div>
              </div>
            )}

            {searchError && (
              <div className='mt-4'>
                <ErrorMessage error={searchError} onDismiss={() => setSearchError(null)} />
              </div>
            )}

            {!searching && searchResults.length > 0 && (
              <div className='mt-[18px]'>
                <div className='mb-2.5 text-[11px] font-bold uppercase tracking-[0.08em] text-[#4E6F8A]'>
                  {searchResults.length === 1 ? 'Company found' : 'Select your company'}
                </div>
                <div className='max-h-[22rem] space-y-3 overflow-y-auto'>
                  {searchResults.map((company) => {
                    const isSelected = selectedCompany?.externalId === company.externalId;
                    const addressLine = formatSearchResultAddress(company.address);
                    const countryLabel = countryOptions.find((opt) => opt.value === country)?.label || country;
                    const registrationLine = [company.organizationNumber ? `Registration: ${company.organizationNumber}` : null, countryLabel]
                      .filter(Boolean)
                      .join(' · ');

                    return (
                      <button
                        key={company.externalId ?? company.name}
                        type='button'
                        onClick={() => handleSelectCompany(company)}
                        disabled={lookingUp}
                        className={`flex w-full items-start gap-3.5 rounded-[16px] border-2 bg-white px-[18px] py-4 text-left transition disabled:pointer-events-none ${
                          isSelected ? 'border-[#1E4E80]' : 'border-[#DDE6EF] hover:border-[#B8C9D9]'
                        }`}
                      >
                        <div className='flex h-[38px] w-[38px] shrink-0 items-center justify-center rounded-[10px] bg-[#EBF4FB] text-[#1E4E80]'>
                          <BuildingIcon className='h-[18px] w-[18px]' />
                        </div>
                        <div className='min-w-0 flex-1'>
                          <div className='mb-1 text-[14.5px] font-semibold text-[#0B1929]'>{company.name ?? '—'}</div>
                          {registrationLine && <div className='text-[13px] leading-relaxed text-[#4E6F8A]'>{registrationLine}</div>}
                          {addressLine && <div className='text-[13px] text-[#4E6F8A]'>{addressLine}</div>}
                        </div>
                        <div className='mt-0.5 shrink-0'>
                          {isSelected ? (
                            <div className='flex h-[22px] w-[22px] items-center justify-center rounded-full bg-[#1E4E80]'>
                              <svg width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='#fff' strokeWidth='3.5' strokeLinecap='round' strokeLinejoin='round' aria-hidden='true'>
                                <polyline points='20 6 9 17 4 12' />
                              </svg>
                            </div>
                          ) : (
                            <div className='h-[22px] w-[22px] rounded-full border-2 border-[#DDE6EF]' />
                          )}
                        </div>
                      </button>
                    );
                  })}
                </div>

                {selectedCompany && (
                  <button
                    type='button'
                    onClick={() => handleUseCompany(selectedCompany)}
                    disabled={lookingUp || isLocked}
                    className={`${primaryButtonClass} mt-3.5`}
                  >
                    {lookingUp ? 'Loading…' : 'Use this company →'}
                  </button>
                )}

                {lookupError && (
                  <div className='mt-3'>
                    <ErrorMessage error={lookupError} onDismiss={() => setLookupError(null)} />
                  </div>
                )}
              </div>
            )}
          </div>

          <p className='mt-5 text-center text-xs text-[#4E6F8A]'>
            Can&apos;t find your company?{' '}
            <button
              type='button'
              onClick={handleEnterManually}
              disabled={isLocked || lookingUp}
              className='font-medium text-[#2E6BAD] hover:underline disabled:cursor-not-allowed disabled:opacity-50'
            >
              Enter details manually →
            </button>
          </p>
        </div>

      </div>
    </div>
  );
}

export default CollectDataCompany;
